import React, { useState, useEffect, useRef, useMemo } from "react";
import { supabase } from "./supabase";
import { dispararActualizacionEnSegundoPlano } from "./servicios/actualizarPresupuestosTotales";
import { filtrarEntradasParaCliente } from "./permisos";
import { usePreventScrollShift } from "./hooks/usePreventScrollShift";
import {
  BarChart3,
  Wallet,
  Calendar,
  FileText,
  TrendingUp,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Download,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  MapPin,
  DollarSign,
  Package,
  Clock,
  Building2,
  User,
  Save,
  X,
  Upload,
  Settings,
} from "lucide-react";

// Configuración anti-zoom OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Funciones helper para manejar fechas sin problemas de timezone
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatMonthForSelect = (monthString) => {
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1, 15);
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "long",
  }).format(date);
};

const formatearCUIT = (cuit) => {
  if (!cuit) return "";
  const numerosSolo = cuit.replace(/\D/g, "");

  // Formateo progresivo en tiempo real
  if (numerosSolo.length <= 2) {
    return numerosSolo;
  } else if (numerosSolo.length <= 10) {
    return `${numerosSolo.slice(0, 2)}-${numerosSolo.slice(2)}`;
  } else {
    return `${numerosSolo.slice(0, 2)}-${numerosSolo.slice(
      2,
      10
    )}-${numerosSolo.slice(10)}`;
  }
};

// CustomDropdown UNIFICADO
// Dropdown personalizado unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
  onSearch,
  canCreate = false,
  onCreateNew,
  createText = "Crear nuevo",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          onWheel={(e) => {
            // Evita que el scroll burbujee al body cuando se llega al límite
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // En mobile, evita scroll-bounce hacia el body
            e.stopPropagation();
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  canCreate &&
                  filteredOptions.length === 0 &&
                  searchTerm.trim()
                ) {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {option.label}
            </button>
          ))}

          {/* Opción de crear nuevo */}
          {canCreate && searchTerm.trim() && filteredOptions.length === 0 && (
            <button
              onClick={() => {
                onCreateNew?.(searchTerm.trim());
                setSearchTerm("");
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "#f0fdf4",
                color: "#166534",
                fontSize: "14px",
                fontWeight: "600",
                textAlign: "left",
                cursor: "pointer",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                touchAction: "manipulation",
              }}
            >
              <Plus size={14} />
              {createText}: "{searchTerm}"
            </button>
          )}

          {/* Mensaje cuando no hay resultados */}
          {!canCreate && filteredOptions.length === 0 && searchTerm && (
            <div
              style={{
                padding: "16px",
                textAlign: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal personalizado UNIFICADO
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getConfig = () => {
    switch (type) {
      case "confirm":
        return {
          icon: AlertCircle,
          color: "#eab308",
          bg: "#fefbf3",
          border: "#fed7aa",
        };
      case "error":
        return {
          icon: XCircle,
          color: "#ef4444",
          bg: "#fef2f2",
          border: "#fecaca",
        };
      case "success":
        return {
          icon: CheckCircle2,
          color: "#10b981",
          bg: "#f0fdf4",
          border: "#bbf7d0",
        };
      default:
        return {
          icon: Info,
          color: "#3b82f6",
          bg: "#eff6ff",
          border: "#dbeafe",
        };
    }
  };

  const config = getConfig();
  const IconComponent = config.icon;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        className="modal-content"
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `1px solid ${config.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: config.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <IconComponent size={24} color={config.color} />
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1e293b",
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.5",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            className="button-hover"
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para vista completa de la entrada
function EntradaDetailModal({
  entrada,
  proyectos,
  conceptos,
  todasLosConceptos,
  cajas,
  trabajadores,
  todosLosProveedores,
  constructora,
  onClose,
  isMobile,
}) {
  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  const proyecto = proyectos.find((p) => p.id === entrada.proyecto_id);
  const concepto = todasLosConceptos.find((c) => c.id === entrada.concepto_id);
  const cajaOrigen = cajas.find((c) => c.id === entrada.caja_origen_id);
  const cajaDestino = cajas.find((c) => c.id === entrada.caja_destino_id);
  const trabajador = trabajadores.find((t) => t.id === entrada.trabajador_id);

  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    const proyectoEncontrado = proyectos.find((p) => p.cuit_cliente === cuit);
    if (proyectoEncontrado) {
      return proyectoEncontrado.apellido_cliente;
    }

    const trabajadorEncontrado = trabajadores.find((t) => t.cuit === cuit);
    if (trabajadorEncontrado) {
      return `${trabajadorEncontrado.apellido}, ${trabajadorEncontrado.nombre}`;
    }

    const proveedorEncontrado = todosLosProveedores?.find((p) => p.cuit === cuit);
    if (proveedorEncontrado) {
      return proveedorEncontrado.nombre;
    }

    return "";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Eye size={24} />
            <div>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "700",
                }}
              >
                Vista detallada - Entrada
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                }}
              >
                {proyecto?.codigo} - {formatDateForDisplay(entrada.fecha)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "12px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "16px" : "20px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={20} color="#3b82f6" />
                Información principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Proyecto */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Proyecto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {proyecto?.codigo}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Cliente: {proyecto?.apellido_cliente}
                  </p>
                </div>

                {/* Concepto */}
                <div
                  style={{
                    background: "#eff6ff",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #dbeafe",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#1e40af",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Concepto
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#3b82f6",
                    }}
                  >
                    {concepto?.nombre}
                  </p>
                </div>

                {/* Pago Directo */}
                {entrada.pago_directo_trabajador && trabajador && (
                  <div
                    style={{
                      background: "#fefbf3",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #fed7aa",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#9a3412",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <User size={12} />
                      Pago directo
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#eab308",
                      }}
                    >
                      {trabajador.apellido}, {trabajador.nombre}
                    </p>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#9a3412",
                      }}
                    >
                      Puesto: {trabajador.puesto}
                    </p>
                  </div>
                )}

                {/* Monto */}
                <div
                  style={{
                    background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
                    padding: "20px",
                    borderRadius: "12px",
                    border: "2px solid #10b981",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#166534",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Monto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: "#10b981",
                    }}
                  >
                    +{entrada.moneda} ${Number(entrada.monto).toLocaleString()}
                  </p>
                  {entrada.tipo_cambio && entrada.moneda === "USD" && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#64748b",
                      }}
                    >
                      Tipo de cambio: ${entrada.tipo_cambio}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Información Adicional */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={20} color="#3b82f6" />
                Detalles adicionales
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Fecha
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Calendar size={16} />
                    {formatDateForDisplay(entrada.fecha)}
                  </p>
                </div>

                {/* Origen del pago */}
                <div
                  style={{
                    background: cajaOrigen ? "#eff6ff" : "#f0fdf4",
                    padding: "16px",
                    borderRadius: "12px",
                    border: `1px solid ${cajaOrigen ? "#dbeafe" : "#dcfce7"}`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: cajaOrigen ? "#1e40af" : "#166534",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Origen del pago
                  </p>
                  {cajaOrigen ? (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Wallet size={16} />
                      {cajaOrigen.nombre} {cajaOrigen.moneda}
                    </p>
                  ) : (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <User size={16} />
                      Cliente: {proyecto?.apellido_cliente}
                    </p>
                  )}
                </div>

                {/* Destino del pago */}
                {cajaDestino && (
                  <div
                    style={{
                      background: "#eff6ff",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #dbeafe",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#1e40af",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Destino del pago
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#3b82f6",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Wallet size={16} />
                      {cajaDestino.nombre} {cajaDestino.moneda}
                    </p>
                  </div>
                )}

                {/* Método de pago */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Método de pago
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      textTransform: "capitalize",
                    }}
                  >
                    {entrada.metodo_pago}
                  </p>
                </div>

                {/* Descripción */}
                {entrada.descripcion && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Descripción
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {entrada.descripcion}
                    </p>
                  </div>
                )}

                {/* Datos de facturación */}
                {entrada.tiene_factura &&
                  (entrada.facturador || entrada.factura_a_nombre_de) && (
                    <div
                      style={{
                        background: "#fefbf3",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "1px solid #fed7aa",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#eab308",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={16} />
                        Datos de facturación
                      </p>

                      {entrada.facturador && (
                        <div style={{ marginBottom: "12px" }}>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Facturador
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {entrada.facturador}
                          </p>
                          {obtenerNombrePorCuit(entrada.facturador) && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#10b981",
                                fontWeight: "500",
                              }}
                            >
                              {obtenerNombrePorCuit(entrada.facturador)}
                            </p>
                          )}
                        </div>
                      )}

                      {entrada.factura_a_nombre_de && (
                        <div>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Factura a nombre de
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {entrada.factura_a_nombre_de}
                          </p>
                          {obtenerNombrePorCuit(
                            entrada.factura_a_nombre_de
                          ) && (
                            <p
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#10b981",
                                fontWeight: "500",
                              }}
                            >
                              {obtenerNombrePorCuit(
                                entrada.factura_a_nombre_de
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Documentos adjuntos */}
          {(entrada.tiene_comprobante || entrada.tiene_factura) && (
            <div
              style={{
                marginTop: "32px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={20} color="#3b82f6" />
                Documentos adjuntos
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {entrada.tiene_comprobante && entrada.comprobante_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #10b981",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={16} />
                        Comprobante
                      </span>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => {
                            const proyecto = proyectos.find(
                              (p) => p.id === entrada.proyecto_id
                            );
                            const fileName = `comprobante_${
                              proyecto?.codigo || "entrada"
                            }_${formatDateForDisplay(entrada.fecha).replace(
                              /\//g,
                              "-"
                            )}.${entrada.comprobante_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = entrada.comprobante_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Download size={12} />
                        </button>
                        <button
                          onClick={() =>
                            window.open(entrada.comprobante_url, "_blank")
                          }
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={12} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open(entrada.comprobante_url, "_blank")
                      }
                    >
                      {entrada.comprobante_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={entrada.comprobante_url}
                          alt="Comprobante"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText
                            size={32}
                            style={{ marginBottom: "8px", opacity: 0.7 }}
                          />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {entrada.tiene_factura && entrada.factura_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #eab308",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #eab308 0%, #d97706 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={16} />
                        Factura
                      </span>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {entrada.tipo_factura && (
                          <span style={{
                            background: entrada.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                            color: entrada.tipo_factura === 'A' ? '#166534' : '#92400e',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {entrada.tipo_factura}
                          </span>
                        )}
                        <button
                          onClick={() => {
                            const proyecto = proyectos.find(
                              (p) => p.id === entrada.proyecto_id
                            );
                            const fileName = `factura_${
                              proyecto?.codigo || "entrada"
                            }_${formatDateForDisplay(entrada.fecha).replace(
                              /\//g,
                              "-"
                            )}.${entrada.factura_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = entrada.factura_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Download size={12} />
                        </button>
                        <button
                          onClick={() =>
                            window.open(entrada.factura_url, "_blank")
                          }
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={12} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => window.open(entrada.factura_url, "_blank")}
                    >
                      {entrada.factura_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={entrada.factura_url}
                          alt="Factura"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText
                            size={32}
                            style={{ marginBottom: "8px", opacity: 0.7 }}
                          />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal para visualizar documento individual con zoom y scroll
function DocumentViewModal({ url, tipo, fileName, onClose, isMobile }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName || `${tipo}_${Date.now()}.${url.split(".").pop()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error descargando archivo:", error);
    }
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.3, 4));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.3, 0.5));
  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = url.match(/\.pdf$/i);

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2500,
        padding: "10px",
        animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        className="document-modal"
        style={{
          background: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "95vw",
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header con controles */}
        <div
          style={{
            background:
              tipo === "factura"
                ? "linear-gradient(135deg, #eab308 0%, #d97706 100%)"
                : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "white",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FileText size={20} />
            <div>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600" }}>
                {tipo === "factura" ? "Factura" : "Comprobante"}
              </h3>
              <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>
                Click y arrastra para mover • Rueda del mouse para zoom
              </p>
            </div>
          </div>

          {/* Controles de zoom para imágenes */}
          {isImage && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="zoom-button"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "14px",
                  cursor: scale <= 0.5 ? "not-allowed" : "pointer",
                  opacity: scale <= 0.5 ? 0.5 : 1,
                  fontWeight: "600",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                −
              </button>
              <span
                style={{
                  fontSize: "13px",
                  minWidth: "50px",
                  textAlign: "center",
                  fontWeight: "600",
                }}
              >
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 4}
                className="zoom-button"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  color: "white",
                  fontSize: "14px",
                  cursor: scale >= 4 ? "not-allowed" : "pointer",
                  opacity: scale >= 4 ? 0.5 : 1,
                  fontWeight: "600",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                +
              </button>
              <button
                onClick={handleResetZoom}
                className="zoom-button"
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  color: "white",
                  fontSize: "11px",
                  cursor: "pointer",
                  fontWeight: "600",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                Reset
              </button>
            </div>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDownload}
              className="button-hover"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "8px",
                padding: "10px 14px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <Download size={14} />
              Descargar
            </button>
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "8px",
                padding: "10px 14px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#000",
            overflow: isImage ? "hidden" : "auto",
            position: "relative",
            cursor: isDragging ? "grabbing" : scale > 1 ? "grab" : "default",
            minHeight: "400px",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={(e) => {
            if (isImage) {
              e.preventDefault();
              if (e.deltaY < 0) {
                handleZoomIn();
              } else {
                handleZoomOut();
              }
            }
          }}
        >
          {isImage ? (
            <img
              src={url}
              alt={tipo}
              style={{
                maxWidth: scale === 1 ? "100%" : "none",
                maxHeight: scale === 1 ? "100%" : "none",
                transform: `scale(${scale}) translate(${
                  position.x / scale
                }px, ${position.y / scale}px)`,
                transition: isDragging ? "none" : "transform 0.2s ease",
                objectFit: "contain",
                userSelect: "none",
                pointerEvents: "none",
              }}
              draggable={false}
            />
          ) : isPDF ? (
            <iframe
              src={url}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: "600px",
              }}
              title={tipo}
            />
          ) : (
            <div
              style={{
                color: "white",
                textAlign: "center",
                padding: "40px",
              }}
            >
              <FileText
                size={64}
                style={{ marginBottom: "20px", opacity: 0.7 }}
              />
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "8px",
                  fontWeight: "600",
                }}
              >
                Archivo no previsualizable
              </p>
              <p
                style={{ fontSize: "14px", marginBottom: "24px", opacity: 0.8 }}
              >
                {fileName || "Documento"}
              </p>
              <button
                onClick={handleDownload}
                className="button-hover"
                style={{
                  background: tipo === "factura" ? "#eab308" : "#10b981",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: "0 auto",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                <Download size={16} />
                Descargar archivo
              </button>
            </div>
          )}
        </div>

        {/* Footer con información */}
        {isImage && scale > 1 && (
          <div
            style={{
              background: "#f8fafc",
              padding: "8px 16px",
              fontSize: "12px",
              color: "#64748b",
              textAlign: "center",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            <Info size={12} />
            Arrastra para mover la imagen • Usa la rueda del mouse para hacer
            zoom
          </div>
        )}
      </div>
    </div>
  );
}

function Entradas({ constructora, usuario, permisosCliente }) {
  const [entradas, setEntradas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [todasLosConceptos, setTodasLosConceptos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [todosLosProveedores, setTodosLosProveedores] = useState([]);
  // Trabajador inactivo seleccionado (se agregará a la lista si falta)
  const [cargandoTrabajadorSeleccionado, setCargandoTrabajadorSeleccionado] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('entradas_filterProject');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return "todos";
    }
  });
  const [filterMonth, setFilterMonth] = useState("todos");
  const [filterEstado, setFilterEstado] = useState("activos");
  
  // Estados para el sistema de filtros con engranaje
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterConcepto, setFilterConcepto] = useState("todos");
  const [filterCaja, setFilterCaja] = useState("todos");
  const [filterTrabajador, setFilterTrabajador] = useState("todos");
  const [filterComision, setFilterComision] = useState("todos");
  
  const [entradasFiltradas, setEntradasFiltradas] = useState([]);
  const [isClient, setIsClient] = useState(false);

  // Estados para modales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEntradaDetail, setSelectedEntradaDetail] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Función para actualizar conceptos
  const actualizarConceptos = (nuevoConcepto) => {
    setConceptos((prev) =>
      [...prev, nuevoConcepto].sort((a, b) => a.nombre.localeCompare(b.nombre))
    );
  };

  // Determinar permisos
  const isCliente = usuario?.tipo_usuario === "cliente";
  const isConstructoraOrEmpleado =
    usuario?.tipo_usuario === "constructora" ||
    usuario?.tipo_usuario === "empleado";
  const puedeEditar = isConstructoraOrEmpleado;
  const puedeCrear = isConstructoraOrEmpleado;
  const puedeEliminar = isConstructoraOrEmpleado;

  // Función para buscar nombres por CUIT en el contexto de Entradas
  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    const proyectoEncontrado = proyectos.find((p) => p.cuit_cliente === cuit);
    if (proyectoEncontrado) {
      return proyectoEncontrado.apellido_cliente;
    }

    const trabajadorEncontrado = trabajadores.find((t) => t.cuit === cuit);
    if (trabajadorEncontrado) {
      return `${trabajadorEncontrado.apellido}, ${trabajadorEncontrado.nombre}`;
    }

    const proveedorEncontrado = todosLosProveedores?.find((p) => p.cuit === cuit);
    if (proveedorEncontrado) {
      return proveedorEncontrado.nombre;
    }

    return "";
  };

  useEffect(() => {
    setIsClient(true);
    setIsMobile(window.innerWidth <= 768);

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora, filterEstado]);

  useEffect(() => {
    aplicarFiltros();
  }, [
    entradas,
    searchTerm,
    filterProject,
    filterMonth,
    filterEstado,
    filterConcepto,
    filterCaja,
    filterTrabajador,
    filterComision,
    permisosCliente,
  ]);


  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('entradas_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  const openDetailModal = (entrada) => {
    setSelectedEntradaDetail(entrada);
    setShowDetailModal(true);
  };

  const openDocumentModal = (url, tipo, entrada) => {
    const proyecto = proyectos.find((p) => p.id === entrada.proyecto_id);
    const fileName = `${tipo}_${
      proyecto?.codigo || "entrada"
    }_${formatDateForDisplay(entrada.fecha).replace(/\//g, "-")}.${url
      .split(".")
      .pop()}`;

    setSelectedDocument(url);
    setSelectedDocumentType(tipo);
    setShowDocumentModal(true);
  };

  const cargarDatos = async () => {
    try {
      // Cargar entradas con datos relacionados incluyendo comisiones
      let query = supabase
        .from("entradas")
        .select(
          `
          *,
          conceptos_entrada (nombre),
          proyectos (codigo, apellido_cliente),
          trabajadores (nombre, apellido, puesto),
          comisiones!comision_relacionada_id (
            id,
            monto_comision,
            moneda,
            salidas (
              proveedores_servicios (nombre),
              proyectos (codigo, apellido_cliente)
            )
          )
        `
        )
        .eq("constructora_id", constructora.id);

      // Filtro por proyecto para clientes
      if (isCliente && permisosCliente?.proyectoId) {
        query = query.eq("proyecto_id", permisosCliente.proyectoId);
      }

      // Filtro por estado (activos / eliminados)
      // Los clientes solo ven elementos activos, no pueden usar filtros de estado
      if (isCliente) {
        query = query.eq("activo", true).is("deleted_at", null);
      } else {
        // Constructoras pueden usar filtros de estado
        if (filterEstado === "activos") {
          query = query.eq("activo", true).is("deleted_at", null);
        } else if (filterEstado === "eliminados") {
          query = query.or("activo.eq.false,deleted_at.not.is.null");
        }
      }

      const { data: entradasData, error: entradasError } = await query.order(
        "fecha",
        { ascending: false }
      );

      if (entradasError) {
        console.error("Error cargando entradas:", entradasError);
      } else {
        setEntradas(entradasData || []);
      }

      // Cargar proyectos según permisos
      let proyectosData = [];
      let proyectosError = null;

      if (filterEstado === "eliminados") {
        // Obtener IDs de proyectos que tengan entradas eliminadas
        const { data: idsData, error: idsError } = await supabase
          .from("entradas")
          .select("proyecto_id")
          .eq("constructora_id", constructora.id)
          .or("activo.eq.false,deleted_at.not.is.null");

        if (idsError) {
          proyectosError = idsError;
        } else {
          const ids = Array.from(
            new Set((idsData || []).map((r) => r.proyecto_id).filter(Boolean))
          );
          let query = supabase
            .from("proyectos")
            .select("*")
            .eq("constructora_id", constructora.id);

          if (isCliente && permisosCliente?.proyectoId) {
            // Si es cliente, limitar a su proyecto si tiene eliminados
            if (ids.includes(permisosCliente.proyectoId)) {
              query = query.eq("id", permisosCliente.proyectoId);
            } else {
              // No tiene eliminados: lista vacía
              proyectosData = [];
              query = null;
            }
          } else if (ids.length > 0) {
            query = query.in("id", ids).order("codigo");
          } else {
            proyectosData = [];
            query = null;
          }

          if (query) {
            const { data, error } = await query;
            proyectosData = data || [];
            proyectosError = error || null;
          }
        }
      } else {
        // Activos: solo proyectos activos/no eliminados
        let query = supabase
          .from("proyectos")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null);

        if (isCliente && permisosCliente?.proyectoId) {
          query = query.eq("id", permisosCliente.proyectoId);
        } else {
          query = query.order("codigo");
        }

        const { data, error } = await query;
        proyectosData = data || [];
        proyectosError = error || null;
      }

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);
        if (isCliente && permisosCliente?.proyectoId) {
          setFilterProject(permisosCliente.proyectoId);
        }
      }

      // Cargar conceptos de entrada (solo activos para dropdowns)
      const { data: conceptosData, error: conceptosError } = await supabase
        .from("conceptos_entrada")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (conceptosError) {
        console.error("Error cargando conceptos:", conceptosError);
      } else {
        setConceptos(conceptosData || []);
      }

      // Cargar TODOS los conceptos (sin filtros) para entradas existentes
      const { data: todasLosConceptosData, error: todasLosConceptosError } =
        await supabase
          .from("conceptos_entrada")
          .select("*")
          .eq("constructora_id", constructora.id)
          .order("nombre");

      if (todasLosConceptosError) {
        console.error(
          "Error cargando todos los conceptos:",
          todasLosConceptosError
        );
      } else {
        setTodasLosConceptos(todasLosConceptosData || []);
      }

      // Cargar cajas ACTIVAS para dropdowns
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select(
          `
          *,
          proyectos (codigo)
        `
        )
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }

      // Cargar trabajadores activos
      const { data: trabajadoresData, error: trabajadoresError } =
        await supabase
          .from("trabajadores")
          .select("id, nombre, apellido, puesto, tipo, cuit")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .order("apellido");

      if (trabajadoresError) {
        console.error("Error cargando trabajadores:", trabajadoresError);
      } else {
        setTrabajadores(trabajadoresData || []);
      }

      // Cargar proveedores de servicios
      const { data: proveedoresData, error: proveedoresError } = await supabase
        .from("proveedores_servicios")
        .select("id, nombre, cuit")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (proveedoresError) {
        console.error("Error cargando proveedores:", proveedoresError);
      } else {
        setTodosLosProveedores(proveedoresData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // ✅ FUNCIÓN PARA EXPORTAR A CSV
  const exportarACSV = () => {
    if (entradasFiltradas.length === 0) {
      showCustomAlert("error", "Error", "No hay datos para exportar");
      return;
    }

    // Debug: verificar qué datos tenemos
    console.log("🔍 Debug - Datos para exportar:", entradasFiltradas.slice(0, 2));
    console.log("🔍 Debug - Primera entrada completa:", entradasFiltradas[0]);
    console.log("🔍 Debug - Tipo de usuario:", usuario?.tipo_usuario);

    // Determinar si es cliente para mostrar datos limitados
    const esCliente = usuario?.tipo_usuario === 'cliente';

    // Preparar datos para CSV - DIFERENTES COLUMNAS SEGÚN TIPO DE USUARIO
    const datosCSV = entradasFiltradas.map((entrada) => {
      // Determinar origen y destino
      let origen = '';
      let destino = '';
      
      if (entrada.caja_origen_id) {
        // Si tiene caja_origen_id, mostrar nombre de la caja
        const cajaOrigen = cajas?.find((c) => c.id === entrada.caja_origen_id);
        origen = cajaOrigen?.nombre || '';
      } else {
        // Si no tiene caja_origen_id, fue pago directo del cliente
        origen = entrada.proyectos?.apellido_cliente || 'Cliente Directo';
      }
      
      if (entrada.caja_destino_id) {
        // Si tiene caja_destino_id, mostrar nombre de la caja
        const cajaDestino = cajas?.find((c) => c.id === entrada.caja_destino_id);
        destino = cajaDestino?.nombre || '';
      } else {
        // Si no tiene caja_destino_id, fue pago directo al cliente
        destino = entrada.proyectos?.apellido_cliente || 'Cliente Directo';
      }
      
      if (esCliente) {
        // CSV LIMITADO PARA CLIENTES
        return {
          'Fecha': formatDateForDisplay(entrada.fecha),
          'Descripción': entrada.descripcion || '',
          'Monto': parseFloat(entrada.monto).toLocaleString('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }),
          'Moneda': entrada.moneda,
          'Origen': origen,
          'Destino': destino,
          'Concepto Entrada': entrada.conceptos_entrada?.nombre || '',
          'Tiene Factura': entrada.tiene_factura ? 'Sí' : 'No',
          'Facturador': entrada.facturador || '',
          'Factura a Nombre de': entrada.factura_a_nombre_de || '',
          'Tipo de Factura': entrada.tipo_factura || '',
          'Método de Pago': entrada.metodo_pago || '',
          'Tipo de Cambio': entrada.tipo_cambio || ''
        };
      } else {
        // CSV COMPLETO PARA CONSTRUCTORAS
        return {
          // Identificación básica
          'ID': entrada.id,
          'Constructora': constructora.nombre,
          'Proyecto Código': entrada.proyectos?.codigo || '',
          'Origen': origen,
          'Destino': destino,
          'Concepto Entrada': entrada.conceptos_entrada?.nombre || '',
          'Trabajador': entrada.trabajadores ? `${entrada.trabajadores.apellido}, ${entrada.trabajadores.nombre}` : '',
          
          // Datos principales
          'Fecha': formatDateForDisplay(entrada.fecha),
          'Descripción': entrada.descripcion || '',
          'Monto': parseFloat(entrada.monto).toLocaleString('es-AR', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          }),
          'Moneda': entrada.moneda,
          'Tipo de Cambio': entrada.tipo_cambio || '',
          'Método de Pago': entrada.metodo_pago || '',
          
          // Relaciones adicionales
          'Cliente': entrada.proyectos?.apellido_cliente || '',
          
          // Documentos
          'Tiene Factura': entrada.tiene_factura ? 'Sí' : 'No',
          'Factura URL': entrada.factura_url || '',
          'Facturador': entrada.facturador || '',
          'Factura a Nombre de': entrada.factura_a_nombre_de || '',
          'Tipo de Factura': entrada.tipo_factura || '',
          
          // Auditoría
          'Creado por': entrada.usuarios_created?.email || entrada.created_by || '',
          'Fecha de Creación': entrada.created_at ? new Date(entrada.created_at).toLocaleString('es-AR') : '',
          'Actualizado por': entrada.usuarios_updated?.email || entrada.updated_by || '',
          'Fecha de Actualización': entrada.updated_at ? new Date(entrada.updated_at).toLocaleString('es-AR') : '',
          'Eliminado por': entrada.usuarios_deleted?.email || entrada.deleted_by || '',
          'Fecha de Eliminación': entrada.deleted_at ? new Date(entrada.deleted_at).toLocaleString('es-AR') : '',
          'Estado': entrada.activo ? 'Activo' : 'Eliminado',
          'Razón de Eliminación': entrada.deleted_reason || '',
          'Vía de Eliminación': entrada.deleted_via || '',
          'Batch ID de Eliminación': entrada.deleted_batch_id || ''
        };
      }
    });

    // Crear CSV
    const headers = Object.keys(datosCSV[0]);
    const csvContent = [
      headers.join(','),
      ...datosCSV.map(row => 
        headers.map(header => {
          const value = row[header];
          // Manejar valores null/undefined
          const stringValue = value !== null && value !== undefined ? value.toString() : '';
          // Escapar comillas y envolver en comillas si contiene coma o salto de línea
          const escapedValue = stringValue.replace(/"/g, '""');
          return escapedValue.includes(',') || escapedValue.includes('\n') ? `"${escapedValue}"` : escapedValue;
        }).join(',')
      )
    ].join('\n');

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `entradas_${constructora.nombre}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const tipoUsuario = esCliente ? "cliente" : "constructora";
    showCustomAlert("success", "Éxito", `Se exportaron ${entradasFiltradas.length} entradas a CSV (vista de ${tipoUsuario})`);
  };

  // Asegurar que el dropdown (en edición) incluya el trabajador referenciado aunque esté inactivo
  useEffect(() => {
    const ensureSelectedWorkerPresent = async () => {
      try {
        if (!editingEntry?.trabajador_id) return;
        const yaExiste = trabajadores.some(
          (t) => t.id === editingEntry.trabajador_id
        );
        if (yaExiste || cargandoTrabajadorSeleccionado) return;
        setCargandoTrabajadorSeleccionado(true);
        const { data, error } = await supabase
          .from("trabajadores")
          .select("id, nombre, apellido, puesto, tipo, cuit, activo")
          .eq("id", editingEntry.trabajador_id)
          .maybeSingle();
        if (!error && data && !trabajadores.some((t) => t.id === data.id)) {
          setTrabajadores((prev) => [...prev, data]);
        }
      } catch (e) {
        console.error("Error asegurando trabajador seleccionado:", e);
      } finally {
        setCargandoTrabajadorSeleccionado(false);
      }
    };
    ensureSelectedWorkerPresent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEntry?.trabajador_id, trabajadores.length]);

  // Función para detectar si hay filtros activos
  const hayFiltrosActivos = () => {
    return filterMonth !== "todos" || 
           filterEstado !== "activos" || 
           filterConcepto !== "todos" || 
           filterCaja !== "todos" || 
           filterTrabajador !== "todos" || 
           filterComision !== "todos";
  };


  const aplicarFiltros = () => {
    let entradasParaProcesar = [...entradas];

    // Filtro para clientes
    if (isCliente && permisosCliente && permisosCliente.proyectoId) {
      entradasParaProcesar = entradasParaProcesar.filter(
        (entrada) => entrada.proyecto_id === permisosCliente.proyectoId
      );
    }

    // Si es cliente: mostrar solo honorarios y adelantos; ocultar comisiones
    if (isCliente) {
      entradasParaProcesar = entradasParaProcesar.filter((entrada) => {
        const nombreConcepto = (entrada.conceptos_entrada?.nombre || "").toLowerCase();
        const esComision =
          nombreConcepto.includes("comisión") ||
          nombreConcepto.includes("comision") ||
          Boolean(entrada.comision_relacionada_id);

        if (esComision) return false;

        const esPermitido =
          nombreConcepto.includes("honorario") ||
          nombreConcepto.includes("honorarios") ||
          nombreConcepto.includes("adelanto mo") ||
          nombreConcepto.includes("adelanto mano de obra") ||
          nombreConcepto.includes("adelanto de caja") ||
          nombreConcepto.includes("adelanto caja");

        return esPermitido;
      });
    }

    // Otros filtros
    const entradasFiltradas = entradasParaProcesar.filter((entrada) => {
      const matchesSearch =
        entrada.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entrada.proyectos?.codigo
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        entrada.proyectos?.apellido_cliente
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        entrada.trabajadores?.nombre
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        entrada.trabajadores?.apellido
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesProject =
        isCliente ||
        filterProject === "todos" ||
        entrada.proyecto_id === filterProject;
      const matchesMonth =
        filterMonth === "todos" ||
        entrada.fecha.substring(0, 7) === filterMonth;
      const matchesEstado =
        isCliente ||
        filterEstado === "todos" ||
        (filterEstado === "activos" && !entrada.deleted_at) ||
        (filterEstado === "eliminados" && entrada.deleted_at);
      const matchesConcepto =
        filterConcepto === "todos" ||
        entrada.concepto_entrada_id === filterConcepto;
      const matchesCaja =
        filterCaja === "todos" ||
        entrada.caja_destino_id === filterCaja;
      const matchesTrabajador =
        filterTrabajador === "todos" ||
        entrada.trabajador_id === filterTrabajador;
      // Para clientes, reutilizamos filterComision para filtrar por factura
      const matchesComision = isCliente
        ? (
            filterComision === "todos" ||
            (filterComision === "con_factura" && entrada.tiene_factura) ||
            (filterComision === "sin_factura" && !entrada.tiene_factura)
          )
        : (
            filterComision === "todos" ||
            (filterComision === "con_comision" && entrada.comision_relacionada_id) ||
            (filterComision === "sin_comision" && !entrada.comision_relacionada_id)
          );

      return matchesSearch && matchesProject && matchesMonth && matchesEstado && 
             matchesConcepto && matchesCaja && matchesTrabajador && matchesComision;
    });

    setEntradasFiltradas(entradasFiltradas);
  };

  const handleEliminar = async (id) => {
    if (!puedeEliminar) {
      showCustomAlert(
        "error",
        "Sin permisos",
        "No tienes permisos para eliminar entradas"
      );
      return;
    }

    try {
      // Consultar info de la entrada para ajustar el mensaje del modal
      const { data: entradaInfo } = await supabase
        .from("entradas")
        .select("monto, moneda, fecha, cobro_relacionado_id, comision_relacionada_id")
        .eq("id", id)
        .single();

      // Consultar proveedor/comisión (si aplica) para enriquecer el mensaje
      let proveedorNombre = "";
      let estadoComision = null;
      if (entradaInfo?.comision_relacionada_id) {
        const { data: comision } = await supabase
          .from("comisiones")
          .select("id, estado, salida_id, salidas ( proveedores_servicios (nombre) )")
          .eq("id", entradaInfo.comision_relacionada_id)
          .single();
        estadoComision = comision?.estado || null;
        proveedorNombre = comision?.salidas?.proveedores_servicios?.nombre || "";
      }

      const tieneCobro = !!entradaInfo?.cobro_relacionado_id;
      const tieneComision = !!entradaInfo?.comision_relacionada_id;

      const encabezado = tieneComision
        ? `Esta entrada está relacionada con una salida que tiene comisión.\nLa comisión pasará a 'pendiente' y se desvinculará de esta entrada.`
        : `Se eliminará la entrada seleccionada.`;

      const detalles = `\n\nDetalles de la operación:\n- Fecha: ${entradaInfo?.fecha || ""}\n- Monto: ${entradaInfo?.moneda || ""} $${parseFloat(entradaInfo?.monto || 0).toLocaleString()}${proveedorNombre ? `\n- Proveedor: ${proveedorNombre}` : ""}`;

      const notaCobro = tieneCobro
        ? `\n\nAdemás, existe un cobro asociado. Se eliminará y se revertirá su impacto en cajas.`
        : "";

      const mensaje = `${encabezado}${detalles}${notaCobro}\n\n¿Continuar?`;

      showCustomAlert("confirm", "¿Eliminar entrada?", mensaje, async () => {
        try {
          // Si tiene comisión asociada: pasar a pendiente y limpiar vínculo ANTES de borrar la entrada
          if (tieneComision) {
            await supabase
              .from("comisiones")
              .update({
                estado: 'pendiente',
                fecha_cobro: null,
                entrada_relacionada_id: null,
                updated_at: new Date().toISOString(),
              })
              .eq("id", entradaInfo.comision_relacionada_id);
          }

          const { error } = await supabase.rpc("rpc_soft_delete_entrada", {
            p_entrada_id: id,
            p_usuario_id: usuario.id,
            p_razon: null,
          });
          if (error) {
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          } else {
            // ✅ Disparar actualización silenciosa PIA → totales tras eliminar
            try {
              const { data: entradaEliminada } = await supabase
                .from("entradas")
                .select("proyecto_id")
                .eq("id", id)
                .single();
              if (entradaEliminada?.proyecto_id) {
                dispararActualizacionEnSegundoPlano(entradaEliminada.proyecto_id);
              }
            } catch (_) {}

            await cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              tieneComision
                ? "Entrada eliminada. La comisión asociada pasó a 'pendiente' y fue desvinculada."
                : "Entrada eliminada correctamente"
            );
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      });
    } catch (err) {
      // Si falla la preconsulta, usar el flujo estándar
      showCustomAlert(
        "confirm",
        "¿Eliminar entrada?",
        "Se revertirá el impacto en cajas y la entrada quedará eliminada (recuperable). ¿Continuar?",
        async () => {
          try {
            const { error } = await supabase.rpc("rpc_soft_delete_entrada", {
              p_entrada_id: id,
              p_usuario_id: constructora?.usuario_id || null,
              p_razon: null,
            });
            if (error) {
              showCustomAlert(
                "error",
                "Error",
                "Error al eliminar: " + error.message
              );
            } else {
              await cargarDatos();
              showCustomAlert(
                "success",
                "Éxito",
                "Entrada eliminada correctamente"
              );
            }
          } catch (e2) {
            showCustomAlert("error", "Error", "Error: " + e2.message);
          }
        }
      );
    }
  };

  const handleRestaurar = async (id) => {
    try {
      const { data: entradaInfo } = await supabase
        .from("entradas")
        .select("cobro_relacionado_id")
        .eq("id", id)
        .single();

      const tieneCobro = !!entradaInfo?.cobro_relacionado_id;
      const mensaje = tieneCobro
        ? "Esta entrada tiene un cobro asociado. Se restaurarán ambos y se re-aplicará el impacto en cajas. ¿Continuar?"
        : "Se volverá a aplicar el impacto en la caja de destino (y en origen si corresponde).";

      showCustomAlert("confirm", "¿Restaurar entrada?", mensaje, async () => {
        try {
          const { error } = await supabase.rpc("rpc_restore_entrada", {
            p_entrada_id: id,
            p_usuario_id: usuario.id,
          });
          if (error) {
            showCustomAlert(
              "error",
              "Error",
              "Error al restaurar: " + error.message
            );
          } else {
            await cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              "Entrada restaurada correctamente"
            );
          }
        } catch (err) {
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      });
    } catch (err) {
      showCustomAlert(
        "confirm",
        "¿Restaurar entrada?",
        "Se volverá a aplicar el impacto en la caja de destino (y en origen si corresponde).",
        async () => {
          try {
            const { error } = await supabase.rpc("rpc_restore_entrada", {
              p_entrada_id: id,
              p_usuario_id: constructora?.usuario_id || null,
            });
            if (error) {
              showCustomAlert(
                "error",
                "Error",
                "Error al restaurar: " + error.message
              );
            } else {
              await cargarDatos();
              showCustomAlert(
                "success",
                "Éxito",
                "Entrada restaurada correctamente"
              );
            }
          } catch (e2) {
            showCustomAlert("error", "Error", "Error: " + e2.message);
          }
        }
      );
    }
  };

  // Obtener meses únicos de entradas filtradas
  const mesesUnicos = [
    ...new Set(entradasFiltradas.map((e) => e.fecha.substring(0, 7))),
  ]
    .sort()
    .reverse();

  // Preparar opciones para dropdowns
  const opcionesProyectos = proyectos.map((proyecto) => ({
    value: proyecto.id,
    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
      proyecto.obra_finalizada ? " (Finalizado)" : ""
    }`,
  }));

  const opcionesMeses = mesesUnicos.map((mes) => ({
    value: mes,
    label: formatMonthForSelect(mes),
  }));

  // Preparar datos para cards móviles UNIFICADAS
  const prepararDatosParaCardsMobiles = (entrada) => {
    const proyecto = proyectos.find((p) => p.id === entrada.proyecto_id);
    const concepto = todasLosConceptos.find(
      (c) => c.id === entrada.concepto_id
    );
    const trabajador = trabajadores.find((t) => t.id === entrada.trabajador_id);

    const badges = [];
    if (entrada.pago_directo_trabajador) {
      badges.push({
        text: "Directo",
        bg: "#fefbf3",
        color: "#92400e",
        icon: User,
      });
    }

    const documentos = [];
    if (entrada.tiene_comprobante) {
      documentos.push({
        nombre: "Comprobante",
        bg: "#f0fdf4",
        color: "#166534",
      });
    }
    if (entrada.tiene_factura) {
      documentos.push({
        nombre: "Factura",
        bg: "#fefbf3",
        color: "#92400e",
      });
    }

    return {
      id: entrada.id,
      titulo_principal: proyecto?.codigo || "Sin proyecto",
      subtitulo: proyecto?.apellido_cliente || "Sin cliente",
      valor_principal: `${entrada.moneda} $${Number(
        entrada.monto
      ).toLocaleString()}`,
      fecha: formatDateForDisplay(entrada.fecha),
      concepto: concepto?.nombre || "Sin concepto",
      descripcion: entrada.descripcion,
      badges,
      documentos,
      tipo: "entrada",
    };
  };

  // Prevenir scroll shift cuando el modal está abierto
  usePreventScrollShift(showModal);

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header UNIFICADO */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px #2563eb10",
              }}
            >
              <TrendingUp size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isCliente ? "Tus pagos a constructora" : "Gestión de entradas"}
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {isCliente
                  ? `Visualizá y filtrá tus pagos realizados a la constructora${
                      permisosCliente?.proyectoCodigo ? ` del proyecto ${permisosCliente.proyectoCodigo}` : ""
                    }.`
                  : `Gestioná los ingresos de ${constructora?.nombre}`}
              </p>
            </div>
          </div>

          {/* Botones desktop UNIFICADOS */}
          <div style={{ display: isMobile ? "none" : "flex", gap: "12px" }}>
            {/* Botón Nueva entrada (primero) */}
            {puedeCrear && (
              <button
                onClick={() => {
                  setEditingEntry(null);
                  setShowModal(true);
                }}
                style={{
                  background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                  color: "white",
                  border: "none",
                  padding: "12px 20px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 16px #2563eb10",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                <Plus size={16} />
                Nueva entrada
              </button>
            )}

            {/* Botón Exportar CSV (segundo) */}
            <button
              onClick={exportarACSV}
              className="button-hover"
              style={{
                padding: "12px 20px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                background: "white",
                color: "#475569",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <Download size={16} />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Búsqueda y Filtro de Proyecto */}
        <div style={{ 
          marginBottom: "20px",
          display: isMobile ? "grid" : "flex",
          gridTemplateColumns: isMobile ? "1fr auto" : "none",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap"
        }}>
          {/* Campo de búsqueda */}
          <div style={{ 
            flex: isMobile ? "none" : "1 1 auto",
            minWidth: isMobile ? "0" : "200px", 
            marginBottom: "0"
          }}>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#64748b",
                  zIndex: 1,
                }}
              />
              <input
                type="text"
                placeholder="Buscar por proyecto, cliente, trabajador o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus={false}
                autoComplete="off"
                style={{
                  width: "100%",
                  padding: isMobile
                    ? "12px 16px 12px 44px"
                    : "10px 10px 10px 36px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: isMobile ? "16px" : "14px",
                  WebkitAppearance: "none",
                  appearance: "none",
                  background: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxSizing: "border-box",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                }}
              />
            </div>
          </div>

          {/* Botón de Filtros - Solo mobile */}
          {isMobile && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              style={{
                padding: "12px",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                background: showFilterPanel ? "#f1f5f9" : "white",
                color: showFilterPanel ? "#1e293b" : "#475569",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                minWidth: "44px",
                minHeight: "44px",
              }}
            >
              <Settings size={18} />
            </button>
          )}

          {/* Filtro de Proyecto - Solo para constructoras en desktop */}
          {!isCliente && !isMobile && (
            <div style={{ 
              flex: "0 0 280px", 
              minWidth: "280px" 
            }}>
              <CustomDropdown
                value={filterProject}
                onChange={(value) => {
                  setFilterProject(value);
                  guardarFiltroProyecto(value);
                }}
                options={[
                  { value: "todos", label: "Todos los proyectos" },
                  ...opcionesProyectos,
                ]}
                placeholder="Filtrar por proyecto"
                icon={Building2}
              />
            </div>
          )}

          {/* Botón de Filtros - Solo desktop */}
          {!isMobile && (
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              style={{
                background: showFilterPanel ? "#f1f5f9" : (hayFiltrosActivos() ? "#fef3c7" : "white"),
                color: showFilterPanel ? "#1e293b" : (hayFiltrosActivos() ? "#92400e" : "#475569"),
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                position: "relative",
                minHeight: "44px",
                touchAction: "manipulation",
              }}
            >
              <Settings size={16} />
              Filtros
              {hayFiltrosActivos() && (
                <div
                  style={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#f59e0b",
                    borderRadius: "50%",
                  }}
                />
              )}
            </button>
          )}

        </div>

        {/* Panel de Filtros Desplegable */}
        <div
          style={{
            maxHeight: showFilterPanel ? (isMobile ? "500px" : "100px") : "0px",
            overflow: showFilterPanel ? "visible" : "hidden",
            transition: "all 0.25s ease-out",
            opacity: showFilterPanel ? 1 : 0,
            marginBottom: showFilterPanel ? "20px" : "0px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile 
                ? "1fr" 
                : isCliente
                ? "0.8fr 1.2fr 1fr 1fr"
                : "0.8fr 0.6fr 1.2fr 1fr 1fr 1fr",
              gap: isMobile ? "8px" : "12px",
              alignItems: "center",
              transform: `translateY(${showFilterPanel ? '0' : '-8px'})`,
              transition: "transform 0.25s ease-out",
              padding: isMobile && showFilterPanel ? "12px 0" : "0",
            }}
          >
            {/* Filtro de Proyecto - Solo para constructoras */}
            {!isCliente && isMobile && (
              <CustomDropdown
                value={filterProject}
                onChange={(value) => {
                  setFilterProject(value);
                  guardarFiltroProyecto(value);
                }}
                options={[
                  { value: "todos", label: "Todos los proyectos" },
                  ...opcionesProyectos,
                ]}
                placeholder="Filtrar por proyecto"
                icon={Building2}
              />
            )}

            {/* Filtro de Período */}
            <CustomDropdown
              value={filterMonth}
              onChange={setFilterMonth}
              options={[
                { value: "todos", label: "Todos los períodos" },
                ...opcionesMeses,
              ]}
              placeholder="Período"
              icon={Calendar}
            />

            {/* Filtro de Estado */}
            {!isCliente && (
              <CustomDropdown
                value={filterEstado}
                onChange={setFilterEstado}
                options={[
                  { value: "activos", label: "Activos" },
                  { value: "eliminados", label: "Eliminados" },
                ]}
                placeholder="Estado"
                icon={Filter}
              />
            )}

            {/* Filtro de Concepto */}
            <CustomDropdown
              value={filterConcepto}
              onChange={setFilterConcepto}
              options={[
                { value: "todos", label: "Todos los conceptos" },
                ...(
                  isCliente
                    ? conceptos
                        .filter(c => {
                          const n = (c.nombre || "").toLowerCase();
                          const esComision = n.includes("comision") || n.includes("comisión");
                          const esPermitido =
                            n.includes("honorario") ||
                            n.includes("honorarios") ||
                            n.includes("adelanto mo") ||
                            n.includes("adelanto mano de obra") ||
                            n.includes("adelanto de caja") ||
                            n.includes("adelanto caja");
                          if (esComision || !esPermitido) return false;
                          // Mostrar solo conceptos que existan en datos del proyecto del cliente
                          const proyectoId = permisosCliente?.proyectoId || null;
                          const existeEnProyecto = entradas.some(e =>
                            (!proyectoId || e.proyecto_id === proyectoId) && e.concepto_id === c.id
                          );
                          return existeEnProyecto;
                        })
                        .map(c => ({ value: c.id, label: c.nombre }))
                    : conceptos.map(c => ({ value: c.id, label: c.nombre }))
                )
              ]}
              placeholder="Concepto"
              icon={FileText}
            />

            {/* Filtro de Caja - oculto para clientes; visible para constructoras */}
            {!isCliente && (
              <CustomDropdown
                value={filterCaja}
                onChange={setFilterCaja}
                options={[
                  { value: "todos", label: "Todas las cajas" },
                  ...(
                    filterProject && filterProject !== "todos"
                      ? cajas
                          .filter(c => entradas.some(e => e.proyecto_id === filterProject && (e.caja_destino_id === c.id || e.caja_origen_id === c.id)))
                          .map(c => ({ value: c.id, label: c.nombre }))
                      : cajas.map(c => ({ value: c.id, label: c.nombre }))
                  )
                ]}
                placeholder="Caja"
                icon={Wallet}
              />
            )}

            {/* Filtro de Trabajador - oculto para clientes; visible para constructoras */}
            {!isCliente && (
              <CustomDropdown
                value={filterTrabajador}
                onChange={setFilterTrabajador}
                options={[
                  { value: "todos", label: "Trabajadores" },
                  ...(
                    filterProject && filterProject !== "todos"
                      ? trabajadores
                          .filter(t => entradas.some(e => e.proyecto_id === filterProject && e.trabajador_id === t.id))
                          .map(t => ({ value: t.id, label: `${t.nombre} ${t.apellido}` }))
                      : trabajadores.map(t => ({ value: t.id, label: `${t.nombre} ${t.apellido}` }))
                  )
                ]}
                placeholder="Trabajador"
                icon={User}
              />
            )}

            {/* Filtro Tiene Factura - Solo clientes */}
            {isCliente && (
              <CustomDropdown
                value={filterComision}
                onChange={setFilterComision}
                options={[
                  { value: "todos", label: "Todas" },
                  { value: "con_factura", label: "Con factura" },
                  { value: "sin_factura", label: "Sin factura" },
                ]}
                placeholder="Factura"
                icon={FileText}
              />
            )}

            {/* Filtro de Comisión - Solo para constructoras */}
            {!isCliente && (
              <CustomDropdown
                value={filterComision}
                onChange={setFilterComision}
                options={[
                  { value: "todos", label: "Todas las entradas" },
                  { value: "con_comision", label: "Con comisión" },
                  { value: "sin_comision", label: "Sin comisión" },
                ]}
                placeholder="Comisión"
                icon={DollarSign}
              />
            )}
          </div>
        </div>
      </div>

      {/* Stats COMPACTAS - Solo desktop */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {[
            {
              label: "Total entradas",
              value: entradasFiltradas.length.toLocaleString(),
              icon: BarChart3,
              color: "#3b82f6",
            },
            {
              label: "Este mes",
              value: entradasFiltradas
                .filter(
                  (e) =>
                    e.fecha.substring(0, 7) ===
                    new Date().toISOString().substring(0, 7)
                )
                .length.toLocaleString(),
              icon: Calendar,
              color: "#10b981",
            },
            {
              label: "Total ARS",
              value:
                "$" +
                entradasFiltradas
                  .filter((e) => e.moneda === "ARS")
                  .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                  .toLocaleString(),
              icon: DollarSign,
              color: "#0ea5e9",
              subtitle:
                "Total equivalente: $" +
                entradasFiltradas
                  .reduce((sum, s) => {
                    if (s.moneda === "ARS") {
                      return sum + parseFloat(s.monto || 0);
                    } else if (s.moneda === "USD") {
                      return (
                        sum +
                        (parseFloat(s.monto || 0) * parseFloat(s.tipo_cambio || 1))
                      );
                    }
                    return sum;
                  }, 0)
                  .toLocaleString("es-AR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }),
            },
            {
              label: "Total USD",
              value:
                "US$" +
                entradasFiltradas
                  .filter((e) => e.moneda === "USD")
                  .reduce((sum, e) => sum + parseFloat(e.monto), 0)
                  .toLocaleString(),
              icon: DollarSign,
              color: "#10b981",
              subtitle:
                "Total equivalente: US$" +
                entradasFiltradas
                  .reduce((sum, s) => {
                    if (s.moneda === "USD") {
                      return sum + parseFloat(s.monto || 0);
                    } else if (s.moneda === "ARS") {
                      return (
                        sum +
                        (parseFloat(s.monto || 0) / parseFloat(s.tipo_cambio || 1))
                      );
                    }
                    return sum;
                  }, 0)
                  .toLocaleString("es-AR", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }),
            },
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <div>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      margin: 0,
                    }}
                  >
                    {stat.value}
                  </p>
                  {stat.subtitle && (
                    <p
                      style={{
                        fontSize: "9px",
                        fontWeight: "400",
                        color: "#9ca3af",
                        margin: "1px 0 0 0",
                        lineHeight: "1",
                      }}
                    >
                      {stat.subtitle}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lista de Entradas */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : entradasFiltradas.length === 0 ? (
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: isMobile ? "40px 24px" : "80px 40px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "#f1f5f9",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <TrendingUp size={32} color="#64748b" />
          </div>
          <h3
            style={{
              fontSize: isMobile ? "18px" : "20px",
              color: "#1e293b",
              marginBottom: "12px",
              fontWeight: "600",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "No se encontraron entradas"
              : isCliente
              ? "No tienes entradas registradas"
              : "No hay entradas aún"}
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "15px",
              marginBottom:
                searchTerm ||
                filterProject !== "todos" ||
                filterMonth !== "todos"
                  ? "0"
                  : "20px",
              lineHeight: "1.5",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "Intenta cambiar los filtros de búsqueda"
              : isCliente
              ? "Las entradas aparecerán cuando realices pagos a la constructora"
              : "Comienza registrando tu primera entrada"}
          </p>
          {!searchTerm &&
            filterProject === "todos" &&
            filterMonth === "todos" &&
            puedeCrear &&
            !isMobile && (
              <button
                style={{
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: "0 auto",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                <Plus size={16} />
                Crear primera entrada
              </button>
            )}
        </div>
      ) : (
        <div
          className="entries-container"
          style={{
            background: "white",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #f1f5f9",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {isMobile && isClient ? (
            // Vista mobile - Cards UNIFICADAS
            <div style={{ padding: "8px" }}>
              {entradasFiltradas.map((entrada, index) => {
                const cardData = prepararDatosParaCardsMobiles(entrada);
                return (
                  <div
                    key={entrada.id}
                    onClick={() => openDetailModal(entrada)}
                    style={{
                      background: "white",
                      border: "1px solid #f1f5f9",
                      borderRadius: "12px",
                      padding: "16px",
                      marginBottom: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      animation: `slideInRight 0.${
                        5 + (index % 10)
                      }s cubic-bezier(0.4, 0, 0.2, 1)`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      touchAction: "manipulation",
                    }}
                    onTouchStart={(e) => {
                      e.currentTarget.style.transform = "scale(0.98)";
                      e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onTouchEnd={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    {/* HEADER UNIFICADO */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#1e293b",
                            marginBottom: "4px",
                          }}
                        >
                          {cardData.titulo_principal}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            fontWeight: "500",
                          }}
                        >
                          {cardData.subtitulo}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: "right",
                          marginLeft: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "800",
                            color: "#10b981",
                            marginBottom: "2px",
                          }}
                        >
                          {cardData.valor_principal}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "4px",
                          }}
                        >
                          <Calendar size={10} />
                          {cardData.fecha}
                        </div>
                      </div>
                    </div>

                    {/* BADGES UNIFICADOS */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          background: "#eaf1fe",
                          color: "#2563eb",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                        }}
                      >
                        {cardData.concepto}
                      </span>
                      {cardData.badges &&
                        cardData.badges.map((badge, i) => (
                          <span
                            key={i}
                            style={{
                              background: badge.bg,
                              color: badge.color,
                              padding: "2px 6px",
                              borderRadius: "4px",
                              fontSize: "9px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "2px",
                            }}
                          >
                            {badge.icon && <badge.icon size={8} />}
                            {badge.text}
                          </span>
                        ))}
                    </div>

                    {/* DESCRIPCIÓN (si existe) */}
                    {cardData.descripcion && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginBottom: "12px",
                          lineHeight: "1.4",
                        }}
                      >
                        {cardData.descripcion}
                      </div>
                    )}

                    {/* FOOTER UNIFICADO */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: "12px",
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <div style={{ display: "flex", gap: "4px" }}>
                        {cardData.documentos &&
                          cardData.documentos.map((doc, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                              <span
                                style={{
                                  fontSize: "9px",
                                  background: doc.bg,
                                  color: doc.color,
                                  padding: "3px 6px",
                                  borderRadius: "4px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                              >
                                <FileText size={8} />
                                {doc.nombre}
                              </span>
                              {doc.nombre === "Factura" && entrada.tipo_factura && (
                                <span style={{
                                  background: entrada.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                                  color: entrada.tipo_factura === 'A' ? '#166534' : '#92400e',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  fontSize: '8px',
                                  fontWeight: '600',
                                  flexShrink: 0
                                }}>
                                  {entrada.tipo_factura}
                                </span>
                              )}
                            </div>
                          ))}
                      </div>

                      <div style={{ display: "flex", gap: "4px" }}>
                        {puedeEditar && filterEstado === "activos" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEntry(entrada);
                              setShowModal(true);
                            }}
                            style={{
                              background: "#eff6ff",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#3b82f6",
                              touchAction: "manipulation",
                            }}
                          >
                            <Edit size={12} />
                          </button>
                        )}
                        {puedeEliminar && filterEstado === "activos" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEliminar(entrada.id);
                            }}
                            style={{
                              background: "#fef2f2",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#ef4444",
                              touchAction: "manipulation",
                            }}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        {puedeEliminar && filterEstado === "eliminados" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRestaurar(entrada.id);
                            }}
                            style={{
                              background: "#f0fdf4",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#22c55e",
                              touchAction: "manipulation",
                            }}
                          >
                            <Package size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Vista desktop - Tabla profesional
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "1000px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Fecha
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Proyecto
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Concepto
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Monto
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Descripción
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Documentos
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                      }}
                    >
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entradasFiltradas.map((entrada, index) => (
                    <tr
                      key={entrada.id}
                      style={{
                        borderBottom:
                          index < entradasFiltradas.length - 1
                            ? "1px solid #f1f5f9"
                            : "none",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        animation: `slideInRight 0.${
                          5 + (index % 10)
                        }s cubic-bezier(0.4, 0, 0.2, 1)`,
                      }}
                      onMouseEnter={(e) => {
                        e.target.parentElement.style.backgroundColor =
                          "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.target.parentElement.style.backgroundColor =
                          "transparent";
                      }}
                    >
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#1e293b",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Calendar size={16} color="#64748b" />
                          {formatDateForDisplay(entrada.fecha)}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#1e293b",
                          fontSize: "14px",
                        }}
                      >
                        <div>
                          <div
                            style={{ fontWeight: "600", marginBottom: "4px" }}
                          >
                            {entrada.proyectos?.codigo}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#64748b",
                              fontWeight: "400",
                            }}
                          >
                            {entrada.proyectos?.apellido_cliente}
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#64748b",
                          fontSize: "14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              background: "#eaf1fe",
                              color: "#2563eb",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontSize: "11px",
                              fontWeight: "600",
                            }}
                          >
                            {entrada.conceptos_entrada?.nombre}
                          </span>
                          {entrada.pago_directo_trabajador && (
                            <span
                              style={{
                                background: "#fefbf3",
                                color: "#92400e",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "9px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title={`Pago directo a ${entrada.trabajadores?.apellido}, ${entrada.trabajadores?.nombre}`}
                            >
                              <User size={10} />
                              Directo
                            </span>
                          )}
                          {entrada.comisiones && (
                            <span
                              style={{
                                background: "#e0f2fe",
                                color: "#0369a1",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                fontSize: "9px",
                                fontWeight: "600",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title={`${entrada.comisiones.salidas?.proveedores_servicios?.nombre} • ${entrada.comisiones.salidas?.proyectos?.codigo}`}
                            >
                              <DollarSign size={10} />
                              {`${entrada.comisiones.salidas?.proveedores_servicios?.nombre || 'Proveedor'} • ${entrada.comisiones.salidas?.proyectos?.codigo || 'Proyecto'}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "right",
                          fontSize: "14px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "700",
                            color: "#10b981",
                            marginBottom: "4px",
                          }}
                        >
                          {entrada.moneda} $
                          {Number(entrada.monto).toLocaleString()}
                        </div>
                        {entrada.tipo_cambio && entrada.moneda === "USD" && (
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#64748b",
                              fontWeight: "400",
                            }}
                          >
                            TC: ${entrada.tipo_cambio}
                          </div>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#64748b",
                          fontSize: "13px",
                          maxWidth: "200px",
                        }}
                      >
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {entrada.descripcion || "-"}
                        </div>
                        {entrada.pago_directo_trabajador &&
                          entrada.trabajadores && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#eab308",
                                fontWeight: "500",
                                marginTop: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <User size={10} />
                              {entrada.trabajadores.apellido},{" "}
                              {entrada.trabajadores.nombre}
                            </div>
                          )}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          color: "#64748b",
                          fontSize: "13px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            flexWrap: "wrap",
                          }}
                        >
                          {entrada.tiene_comprobante && (
                            <span
                              onClick={() =>
                                openDocumentModal(
                                  entrada.comprobante_url,
                                  "comprobante",
                                  entrada
                                )
                              }
                              className="document-badge"
                              style={{
                                fontSize: "10px",
                                background: "#f0fdf4",
                                color: "#166534",
                                padding: "4px 10px",
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                border: "1px solid #bbf7d0",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                fontWeight: "500",
                              }}
                            >
                              <FileText size={10} />
                              Comprobante
                            </span>
                          )}
                          {entrada.tiene_factura && (
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <span
                                onClick={() =>
                                  openDocumentModal(
                                    entrada.factura_url,
                                    "factura",
                                    entrada
                                  )
                                }
                                className="document-badge"
                                style={{
                                  fontSize: "10px",
                                  background: "#fefbf3",
                                  color: "#92400e",
                                  padding: "4px 10px",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  transition:
                                    "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                  border: "1px solid #fed7aa",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontWeight: "500",
                                }}
                              >
                                <FileText size={10} />
                                Factura
                              </span>
                              {entrada.tipo_factura && (
                                <span style={{
                                  background: entrada.tipo_factura === 'A' ? '#dcfce7' : '#fef3c7',
                                  color: entrada.tipo_factura === 'A' ? '#166534' : '#92400e',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  flexShrink: 0
                                }}>
                                  {entrada.tipo_factura}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailModal(entrada);
                            }}
                            className="action-button"
                            style={{
                              background: "#eff6ff",
                              border: "none",
                              borderRadius: "8px",
                              padding: "8px 10px",
                              cursor: "pointer",
                              color: "#3b82f6",
                              fontSize: "12px",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              touchAction: "manipulation",
                            }}
                          >
                            <Eye size={14} />
                          </button>

                          {puedeEditar && filterEstado === "activos" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingEntry(entrada);
                                setShowModal(true);
                              }}
                              className="action-button"
                              style={{
                                background: "#f8fafc",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 10px",
                                cursor: "pointer",
                                color: "#475569",
                                fontSize: "12px",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                touchAction: "manipulation",
                              }}
                            >
                              <Edit size={14} />
                            </button>
                          )}
                          {puedeEliminar && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (filterEstado === "activos")
                                  handleEliminar(entrada.id);
                                else handleRestaurar(entrada.id);
                              }}
                              className="action-button"
                              style={{
                                background:
                                  filterEstado === "activos"
                                    ? "#fef2f2"
                                    : "#f0fdf4",
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 10px",
                                cursor: "pointer",
                                color:
                                  filterEstado === "activos"
                                    ? "#dc2626"
                                    : "#22c55e",
                                fontSize: "12px",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                touchAction: "manipulation",
                              }}
                            >
                              {filterEstado === "activos" ? (
                                <Trash2 size={14} />
                              ) : (
                                <Package size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Botón flotante móvil UNIFICADO */}
      {isMobile && puedeCrear && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingEntry(null);
              setShowModal(true);
            }}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Modal para crear/editar entradas */}
      {showModal && puedeCrear && (
        <EntradaModal
          constructora={constructora}
          usuario={usuario}
          entrada={editingEntry}
          proyectos={proyectos}
          conceptos={conceptos}
          todasLosConceptos={todasLosConceptos}
          cajas={cajas}
          trabajadores={trabajadores}
          todosLosProveedores={todosLosProveedores}
          onClose={() => {
            setShowModal(false);
            setEditingEntry(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingEntry(null);
            cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              editingEntry
                ? "Entrada actualizada correctamente"
                : "Entrada creada correctamente"
            );
          }}
          isMobile={isMobile}
          permisosCliente={permisosCliente}
          showCustomAlert={showCustomAlert}
          onActualizarConceptos={actualizarConceptos}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedEntradaDetail && (
        <EntradaDetailModal
          entrada={selectedEntradaDetail}
          proyectos={proyectos}
          conceptos={conceptos}
          todasLosConceptos={todasLosConceptos}
          cajas={cajas}
          trabajadores={trabajadores}
          todosLosProveedores={todosLosProveedores}
          constructora={constructora}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedEntradaDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal de Documento Individual */}
      {showDocumentModal && selectedDocument && (
        <DocumentViewModal
          url={selectedDocument}
          tipo={selectedDocumentType}
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedDocument(null);
            setSelectedDocumentType(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      {/* CSS OBLIGATORIO del Sistema Unificado */}
      <style jsx>{`
        /* Configuración anti-zoom OBLIGATORIA */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-user-select: none;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
          -webkit-user-select: text;
        }

        @supports (-webkit-touch-callout: none) {
          input[type="text"],
          input[type="number"],
          input[type="date"],
          input[type="search"] {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
            border-radius: 0;
          }
        }

        html {
          -webkit-text-size-adjust: 100%;
          -ms-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        button {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          -webkit-user-select: none;
        }

        .button-hover,
        .action-button,
        .fab-button,
        .dropdown-button {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
          -webkit-user-select: none;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        @media (max-width: 768px) {
          .entries-container {
            -webkit-overflow-scrolling: touch;
          }

          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones UNIFICADAS */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hover effects UNIFICADOS */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .dropdown-button:hover {
          border-color: #3b82f6;
        }

        .dropdown-button:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .document-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .stat-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        /* Mejoras de accesibilidad */
        .button-hover:focus,
        .action-button:focus,
        .fab-button:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Scrollbar personalizado */
        .table-container::-webkit-scrollbar {
          height: 6px;
        }

        .table-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .table-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .table-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}

// Modal para crear/editar entradas - COMPLETO
function EntradaModal({
  constructora,
  usuario,
  entrada,
  proyectos,
  conceptos,
  todasLosConceptos,
  cajas,
  trabajadores,
  todosLosProveedores,
  onClose,
  onSave,
  isMobile,
  permisosCliente,
  showCustomAlert,
  onActualizarConceptos,
}) {
  const [formData, setFormData] = useState({
    proyecto_id: "",
    concepto_id: "",
    monto: "",
    moneda: "ARS",
    fecha: new Date().toISOString().split("T")[0],
    descripcion: "",
    metodo_pago: "efectivo",
    tipo_cambio: "1",
    caja_origen_id: "",
    caja_destino_id: "",
    comprobante_url: "",
    factura_url: "",
    tiene_comprobante: false,
    tiene_factura: false,
    tipo_factura: "A",
    facturador: "",
    factura_a_nombre_de: "",
    pago_directo_trabajador: false,
    trabajador_id: "",
  });

  const [guardando, setGuardando] = useState(false);
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [subiendoFactura, setSubiendoFactura] = useState(false);
  const [dolarOficial, setDolarOficial] = useState(null);
  const [loadingDolar, setLoadingDolar] = useState(false);
  const [cajasDisponibles, setCajasDisponibles] = useState([]);

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Función para manejar input de CUIT con formateo automático
  const handleCUITChange = (value, field) => {
    const cuitFormateado = formatearCUIT(value);
    setFormData((prev) => ({
      ...prev,
      [field]: cuitFormateado,
    }));
  };

  // Función para buscar nombres por CUIT
  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    const proyectoEncontrado = proyectos.find((p) => p.cuit_cliente === cuit);
    if (proyectoEncontrado) {
      return proyectoEncontrado.apellido_cliente;
    }

    const trabajadorEncontrado = trabajadores.find((t) => t.cuit === cuit);
    if (trabajadorEncontrado) {
      return `${trabajadorEncontrado.apellido}, ${trabajadorEncontrado.nombre}`;
    }

    const proveedorEncontrado = todosLosProveedores?.find((p) => p.cuit === cuit);
    if (proveedorEncontrado) {
      return proveedorEncontrado.nombre;
    }

    return "";
  };

  useEffect(() => {
    if (entrada) {
      console.log("🔍 EDITANDO ENTRADA - Tipo de cambio original:", entrada.tipo_cambio);
      setFormData({
        proyecto_id: entrada.proyecto_id || "",
        concepto_id: entrada.concepto_id || "",
        monto: entrada.monto || "",
        moneda: entrada.moneda || "ARS",
        fecha: formatDateForInput(entrada.fecha),
        descripcion: entrada.descripcion || "",
        metodo_pago: entrada.metodo_pago || "transferencia",
        tipo_cambio: entrada.tipo_cambio ? entrada.tipo_cambio.toString() : "1",
        caja_origen_id: entrada.caja_origen_id || "",
        caja_destino_id: entrada.caja_destino_id || "",
        comprobante_url: entrada.comprobante_url || "",
        factura_url: entrada.factura_url || "",
        tiene_comprobante: entrada.tiene_comprobante || false,
        tiene_factura: entrada.tiene_factura || false,
        tipo_factura: entrada.tipo_factura || "A",
        facturador: entrada.facturador || "",
        factura_a_nombre_de: entrada.factura_a_nombre_de || "",
        pago_directo_trabajador: entrada.pago_directo_trabajador || false,
        trabajador_id: entrada.trabajador_id || "",
      });
    } else {
      // Si es cliente, preseleccionar su proyecto
      if (permisosCliente?.proyectoId) {
        setFormData((prev) => ({
          ...prev,
          proyecto_id: permisosCliente.proyectoId,
        }));
      }
    }
  }, [entrada, permisosCliente]);

  useEffect(() => {
    if (!entrada) {
      cargarDolarOficial();
    } else {
      // Si se está editando, NO cargar dólar oficial para evitar pisar el tipo de cambio
      setDolarOficial(null);
    }
  }, [entrada]);
  // ✅ AUTOCOMPLETAR CUIT - SIEMPRE sobrescribir cuando cambia trabajador
  useEffect(() => {
    if (formData.trabajador_id && formData.tiene_factura) {
      const trabajadorSeleccionado = trabajadores.find(
        (t) => t.id === formData.trabajador_id
      );
      if (trabajadorSeleccionado?.cuit) {
        setFormData((prev) => ({
          ...prev,
          facturador: formatearCUIT(trabajadorSeleccionado.cuit),
        }));
        console.log(
          "✅ CUIT actualizado por cambio de trabajador:",
          formatearCUIT(trabajadorSeleccionado.cuit)
        );
      } else {
        // Si el trabajador no tiene CUIT, limpiar el campo
        setFormData((prev) => ({
          ...prev,
          facturador: "",
        }));
        console.log(
          "❌ Trabajador sin CUIT, campo limpiado:",
          trabajadorSeleccionado?.nombre
        );
      }
    }
  }, [formData.trabajador_id, trabajadores]); // Solo cuando cambia trabajador

  // ✅ AUTOCOMPLETAR cuando se MARCA "tiene factura" (solo si no hay trabajador ya seleccionado)
  useEffect(() => {
    if (
      formData.tiene_factura &&
      formData.trabajador_id &&
      !formData.facturador
    ) {
      const trabajadorSeleccionado = trabajadores.find(
        (t) => t.id === formData.trabajador_id
      );
      if (trabajadorSeleccionado?.cuit) {
        setFormData((prev) => ({
          ...prev,
          facturador: formatearCUIT(trabajadorSeleccionado.cuit),
        }));
        console.log(
          "✅ CUIT autocompletado al marcar factura:",
          formatearCUIT(trabajadorSeleccionado.cuit)
        );
      }
    }
  }, [formData.tiene_factura]); // Solo cuando cambia "tiene_factura"
  const calcularEquivalente = () => {
    if (!formData.monto || !formData.tipo_cambio) return null;

    const monto = parseFloat(formData.monto);
    const tipoCambio = parseFloat(formData.tipo_cambio);

    if (formData.moneda === "USD") {
      const equivalenteARS = monto * tipoCambio;
      return `≈ ARS $${equivalenteARS.toLocaleString("es-AR", {
        maximumFractionDigits: 2,
      })}`;
    } else {
      const equivalenteUSD = monto / tipoCambio;
      return `≈ USD $${equivalenteUSD.toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`;
    }
  };

  useEffect(() => {
    if (formData.proyecto_id) {
      const proyectoSeleccionado = proyectos.find(
        (p) => p.id === formData.proyecto_id
      );
      actualizarCajasDisponibles(proyectoSeleccionado);

      if (proyectoSeleccionado && !formData.factura_a_nombre_de) {
        setFormData((prev) => ({
          ...prev,
          factura_a_nombre_de: proyectoSeleccionado.cuit_cliente || "",
        }));
      }
    } else {
      setCajasDisponibles([]);
    }
  }, [formData.proyecto_id, formData.moneda, cajas, proyectos]);

  useEffect(() => {
    // En edición: nunca sobrescribir las cajas ya guardadas
    if (entrada) return;

    if (formData.concepto_id && formData.proyecto_id) {
      const conceptoSeleccionado = conceptos.find(
        (c) => c.id === formData.concepto_id
      );
      const proyectoSeleccionado = proyectos.find(
        (p) => p.id === formData.proyecto_id
      );

      if (conceptoSeleccionado && proyectoSeleccionado) {
        actualizarCajasSegunConcepto(
          conceptoSeleccionado,
          proyectoSeleccionado
        );
      }
    }
  }, [formData.concepto_id, formData.proyecto_id, formData.moneda, entrada]);

  useEffect(() => {
    if (dolarOficial && !entrada) {
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: dolarOficial.toString(),
      }));
    }
  }, [dolarOficial, entrada]);

  const cargarDolarOficial = async () => {
    // SOLO cargar si NO se está editando
    if (entrada) {
      console.log("🚫 NO cargando dólar oficial porque se está editando entrada");
      return;
    }
    
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      const dolarVenta = data.venta;
      setDolarOficial(dolarVenta);

      setFormData((prev) => ({
        ...prev,
        tipo_cambio: dolarVenta.toString(),
      }));
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setDolarOficial(1000);
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: "1000",
      }));
    }
    setLoadingDolar(false);
  };

  const actualizarCajasDisponibles = (proyecto) => {
    if (!proyecto) return;

    const cajasProyecto = cajas.filter((caja) => {
      if (caja.moneda !== formData.moneda) return false;
      if (caja.tipo_caja === "empresa") return true;
      if (caja.proyecto_id === proyecto.id) return true;
      return false;
    });

    const cajasOrdenadas = cajasProyecto.sort((a, b) => {
      if (a.tipo_caja === "empresa" && b.tipo_caja !== "empresa") return -1;
      if (a.tipo_caja !== "empresa" && b.tipo_caja === "empresa") return 1;
      if (a.tipo_caja === "proyecto" && b.tipo_caja === "mano_obra") return -1;
      if (a.tipo_caja === "mano_obra" && b.tipo_caja === "proyecto") return 1;
      return a.nombre.localeCompare(b.nombre);
    });

    setCajasDisponibles(cajasOrdenadas);

    if (formData.caja_origen_id) {
      const cajaOrigenEstaDisponible = cajasOrdenadas.some(
        (caja) => caja.id === formData.caja_origen_id
      );
      if (!cajaOrigenEstaDisponible) {
        setFormData((prev) => ({
          ...prev,
          caja_origen_id: "",
        }));
      }
    }

    if (formData.caja_destino_id) {
      const cajaDestinoEstaDisponible = cajasOrdenadas.some(
        (caja) => caja.id === formData.caja_destino_id
      );
      if (!cajaDestinoEstaDisponible) {
        setFormData((prev) => ({
          ...prev,
          caja_destino_id: "",
        }));
      }
    }
  };

  const actualizarCajasSegunConcepto = (concepto, proyecto) => {
    const buscarCaja = (tipoCaja, moneda) => {
      return cajas.find(
        (caja) =>
          caja.tipo_caja === tipoCaja &&
          caja.moneda === moneda &&
          (tipoCaja === "empresa" || caja.proyecto_id === proyecto.id)
      );
    };

    let cajaDestino = null;
    if (concepto.caja_destino_default === "empresa") {
      cajaDestino = buscarCaja("empresa", formData.moneda);
    } else if (concepto.caja_destino_default === "mano_obra") {
      cajaDestino = buscarCaja("mano_obra", formData.moneda);
    } else {
      cajaDestino = buscarCaja("proyecto", formData.moneda);
    }

    let cajaOrigen = null;
    if (concepto.caja_origen_default === "empresa") {
      cajaOrigen = buscarCaja("empresa", formData.moneda);
    } else if (concepto.caja_origen_default === "proyecto") {
      cajaOrigen = buscarCaja("proyecto", formData.moneda);
    } else if (concepto.caja_origen_default === "mano_obra") {
      cajaOrigen = buscarCaja("mano_obra", formData.moneda);
    } else {
      // cliente => null
      cajaOrigen = null;
    }

    setFormData((prev) => ({
      ...prev,
      caja_destino_id: cajaDestino?.id || "",
      caja_origen_id: cajaOrigen?.id || "",
    }));
  };

  const generarNombreCaja = (caja) => {
    const nombreBase = caja.nombre || `Caja ${caja.tipo_caja}`;
    return `${nombreBase} ${caja.moneda}`;
  };

  // Función para actualizar comisión relacionada cuando se edita una entrada
  const actualizarComisionRelacionada = async (comisionId, entradaData) => {
    try {
      // Primero obtener la comisión para saber qué salida está relacionada
      const { data: comision } = await supabase
        .from("comisiones")
        .select("salida_id")
        .eq("id", comisionId)
        .single();

      if (!comision) {
        console.error("No se encontró la comisión:", comisionId);
        return;
      }

      // Actualizar la comisión - usar el valor numérico directamente de la entrada
      await supabase
        .from("comisiones")
        .update({
          monto_comision: parseFloat(entradaData.monto),
          moneda: entradaData.moneda,
          tipo_cambio: parseFloat(entradaData.tipo_cambio),
          updated_at: new Date().toISOString(),
        })
        .eq("id", comisionId);

      // También actualizar el campo monto_comision en la tabla salidas
      await supabase
        .from("salidas")
        .update({
          monto_comision: parseFloat(entradaData.monto),
          updated_at: new Date().toISOString(),
        })
        .eq("id", comision.salida_id);

      console.log("✅ Comisión y salida relacionadas actualizadas:", comisionId, comision.salida_id);
    } catch (error) {
      console.error("Error actualizando comisión relacionada:", error);
    }
  };


  // Función para crear concepto desde el dropdown (como en Salidas)
  const crearNuevoConcepto = async (nombreConcepto) => {
    try {
      const { data, error } = await supabase
        .from("conceptos_entrada")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nombreConcepto.trim(),
            activo: true,
            caja_destino_default: "proyecto",
            caja_origen_default: "cliente",
          },
        ])
        .select();

      if (error) {
        console.error("Error guardando concepto:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al guardar concepto: " + error.message
        );
      } else {
        const nuevoConceptoCreado = data[0];
        onActualizarConceptos(nuevoConceptoCreado);

        setFormData((prev) => ({
          ...prev,
          concepto_id: nuevoConceptoCreado.id,
        }));

        showCustomAlert("success", "Éxito", "Concepto agregado correctamente");
      }
    } catch (error) {
      console.error("Error:", error);
      showCustomAlert("error", "Error", "Error: " + error.message);
    }
  };

  const handleFileUpload = async (file, tipo) => {
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor selecciona un archivo de imagen o PDF"
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showCustomAlert(
        "error",
        "Error",
        "El archivo es muy grande. Máximo 10MB"
      );
      return;
    }

    if (tipo === "comprobante") {
      setSubiendoComprobante(true);
    } else {
      setSubiendoFactura(true);
    }

    try {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = `${tipo}-${constructora.id}-${timestamp}.${fileExt}`;
      const bucketName = tipo === "comprobante" ? "comprobantes" : "facturas";

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error(`Error en upload de ${tipo}:`, uploadError);
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;

      if (tipo === "comprobante") {
        setFormData((prev) => ({
          ...prev,
          comprobante_url: finalUrl,
          tiene_comprobante: true,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          factura_url: finalUrl,
          tiene_factura: true,
        }));
      }
    } catch (err) {
      console.error(`Error subiendo ${tipo}:`, err);
      showCustomAlert(
        "error",
        "Error",
        `Error al subir ${tipo}: ` + err.message
      );
    }

    if (tipo === "comprobante") {
      setSubiendoComprobante(false);
    } else {
      setSubiendoFactura(false);
    }
  };

  const handleSave = async () => {
    // Validaciones
    if (
      !formData.proyecto_id ||
      !formData.concepto_id ||
      !formData.monto ||
      !formData.fecha ||
      !formData.caja_destino_id
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor completa todos los campos obligatorios"
      );
      return;
    }

    if (parseFloat(formData.monto) <= 0) {
      showCustomAlert("error", "Error", "El monto debe ser mayor a 0");
      return;
    }

    if (!formData.tipo_cambio || parseFloat(formData.tipo_cambio) <= 0) {
      showCustomAlert("error", "Error", "El tipo de cambio debe ser mayor a 0");
      return;
    }

    if (
      formData.tiene_factura &&
      (!formData.facturador || !formData.factura_a_nombre_de)
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Si tiene factura, debe completar el facturador y a nombre de quien se factura"
      );
      return;
    }

    if (formData.pago_directo_trabajador && !formData.trabajador_id) {
      showCustomAlert(
        "error",
        "Error",
        "Si es pago directo, debe seleccionar un trabajador"
      );
      return;
    }

    setGuardando(true);

    try {
      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: formData.proyecto_id,
        concepto_id: formData.concepto_id,
        monto: parseFloat(formData.monto),
        moneda: formData.moneda,
        fecha: formData.fecha,
        descripcion: formData.descripcion.trim() || null,
        metodo_pago: formData.metodo_pago,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        caja_origen_id: formData.caja_origen_id || null,
        caja_destino_id: formData.caja_destino_id,
        comprobante_url: formData.comprobante_url || null,
        factura_url: formData.factura_url || null,
        tiene_comprobante: formData.tiene_comprobante,
        tiene_factura: formData.tiene_factura,
        tipo_factura: formData.tiene_factura ? (formData.tipo_factura || "A") : null,
        facturador: formData.facturador.trim() || null,
        factura_a_nombre_de: formData.factura_a_nombre_de.trim() || null,
        pago_directo_trabajador: formData.pago_directo_trabajador,
        trabajador_id: formData.pago_directo_trabajador
          ? formData.trabajador_id
          : null,
      };

      // Agregar campos de auditoría según si es creación o actualización
      if (entrada) {
        // Actualización: solo updated_by
        dataToSave.updated_by = usuario.id;
        console.log("📝 Actualizando entrada - updated_by:", usuario.id);
      } else {
        // Creación: created_by y updated_by
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        console.log("🆕 Creando entrada - created_by:", usuario.id, "updated_by:", usuario.id);
      }

      let result;
      if (entrada) {
        result = await supabase
          .from("entradas")
          .update(dataToSave)
          .eq("id", entrada.id)
          .select();
      } else {
        result = await supabase.from("entradas").insert([dataToSave]).select();
      }

      // ✅ Disparar actualización silenciosa PIA → totales para el proyecto afectado (resolver proyecto_id de múltiples fuentes)
      try {
        const { data, error } = result || {};
        const proyectoIdBg = (data && data[0]?.proyecto_id)
          || (entrada && entrada.proyecto_id)
          || dataToSave.proyecto_id
          || formData.proyecto_id;
        if (proyectoIdBg) dispararActualizacionEnSegundoPlano(proyectoIdBg);
      } catch (_) {}

      const { data, error } = result;

      if (error) {
        console.error("Error guardando entrada:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        // 🔧 CAMBIO CLAVE: Manejar balances según el tipo de entrada
        if (formData.pago_directo_trabajador) {
          // Para pagos directos: Solo actualizar entrada, el cobro maneja su balance automáticamente
          await actualizarBalanceSoloEntrada();
          if (entrada && entrada.cobro_relacionado_id) {
            await actualizarCobroRelacionado(
              entrada.cobro_relacionado_id,
              data[0]
            );
          } else {
            await crearCobroAutomatico(data[0]);
          }
        } else {
          // Para entradas normales: Actualizar como siempre
          await actualizarBalanceCajas();
        }

        // Actualizar comisión relacionada si existe
        if (entrada && entrada.comision_relacionada_id) {
          await actualizarComisionRelacionada(entrada.comision_relacionada_id, data[0]);
        }

        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };
  const actualizarBalanceSoloEntrada = async () => {
    try {
      const montoOperacion = parseFloat(formData.monto);

      if (entrada) {
        const montoOriginal = parseFloat(entrada.monto);
        const cajaOrigenOriginalId = entrada.caja_origen_id;
        const cajaDestinoOriginalId = entrada.caja_destino_id;
        const monedaOriginal = entrada.moneda;

        if (
          montoOperacion === montoOriginal &&
          formData.caja_origen_id === cajaOrigenOriginalId &&
          formData.caja_destino_id === cajaDestinoOriginalId &&
          formData.moneda === monedaOriginal
        ) {
          return;
        }

        if (cajaDestinoOriginalId) {
          const { data: cajaDestinoOriginal, error: errorDestinoOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaDestinoOriginalId)
              .single();

          if (!errorDestinoOriginal && cajaDestinoOriginal) {
            const balanceDestinoOriginal =
              parseFloat(cajaDestinoOriginal.balance_actual) || 0;
            const balanceDestinoRevertido =
              balanceDestinoOriginal - montoOriginal;

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceDestinoRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaDestinoOriginalId);
          }
        }

        if (cajaOrigenOriginalId) {
          const { data: cajaOrigenOriginal, error: errorOrigenOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaOrigenOriginalId)
              .single();

          if (!errorOrigenOriginal && cajaOrigenOriginal) {
            const balanceOrigenOriginal =
              parseFloat(cajaOrigenOriginal.balance_actual) || 0;
            const balanceOrigenRevertido =
              balanceOrigenOriginal + montoOriginal;

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceOrigenRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaOrigenOriginalId);
          }
        }
      }

      // Aplicar la nueva entrada
      if (formData.caja_destino_id) {
        const { data: cajaDestino, error: errorDestino } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_destino_id)
          .single();

        if (!errorDestino && cajaDestino) {
          const balanceActualDestino =
            parseFloat(cajaDestino.balance_actual) || 0;
          const nuevoBalanceDestino = balanceActualDestino + montoOperacion;

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceDestino,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_destino_id);
        }
      }

      if (
        formData.caja_origen_id &&
        formData.caja_origen_id !== formData.caja_destino_id
      ) {
        const { data: cajaOrigen, error: errorOrigen } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_origen_id)
          .single();

        if (!errorOrigen && cajaOrigen) {
          const balanceActualOrigen =
            parseFloat(cajaOrigen.balance_actual) || 0;
          const nuevoBalanceOrigen = balanceActualOrigen - montoOperacion;

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceOrigen,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_origen_id);
        }
      }
    } catch (err) {
      console.error("Error en actualización de balances de entrada:", err);
    }
  };

  // Función para crear cobro automático
  const crearCobroAutomatico = async (entradaCreada) => {
    try {
      // Buscar caja empresa de la misma moneda
      const cajaEmpresa = cajas.find(
        (caja) =>
          caja.tipo_caja === "empresa" && caja.moneda === formData.moneda
      );

      if (!cajaEmpresa) {
        console.error("No se encontró caja empresa para el cobro automático");
        showCustomAlert(
          "error",
          "Error",
          "Entrada creada pero no se encontró caja empresa para el cobro automático"
        );
        return;
      }

      // Buscar concepto "Honorarios" en conceptos de cobro
      const { data: conceptosCobro, error: conceptosError } = await supabase
        .from("conceptos_cobro")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);

      if (conceptosError) {
        console.error("Error cargando conceptos de cobro:", conceptosError);
        showCustomAlert(
          "error",
          "Error",
          "Entrada creada pero error al cargar conceptos para cobro automático"
        );
        return;
      }

      let conceptoHonorarios = conceptosCobro.find((c) =>
        c.nombre.toLowerCase().includes("honorarios")
      );

      // Si no existe, crear concepto "Honorarios"
      if (!conceptoHonorarios) {
        const { data: nuevoConcepto, error: conceptoError } = await supabase
          .from("conceptos_cobro")
          .insert([
            {
              constructora_id: constructora.id,
              nombre: "Honorarios",
              activo: true,
            },
          ])
          .select()
          .single();

        if (conceptoError) {
          console.error("Error creando concepto Honorarios:", conceptoError);
          showCustomAlert(
            "error",
            "Error",
            "Entrada creada pero no se pudo crear concepto para el cobro: " +
              conceptoError.message
          );
          return;
        }

        conceptoHonorarios = nuevoConcepto;
      }

      // Obtener datos del trabajador incluyendo CUIT
      const trabajadorSeleccionado = trabajadores.find(
        (t) => t.id === formData.trabajador_id
      );

      // Crear el cobro con concepto "Honorarios"
      const cobroData = {
        constructora_id: constructora.id,
        trabajador_id: formData.trabajador_id,
        concepto_id: conceptoHonorarios.id,
        caja_origen_id: cajaEmpresa.id,
        monto: parseFloat(formData.monto),
        moneda: formData.moneda,
        metodo_pago: formData.metodo_pago || "Transferencia",
        fecha: formData.fecha,
        descripcion: `Pago directo desde cliente - ${
          formData.descripcion || "Sin descripción"
        }`,
        comprobante_url: formData.comprobante_url || null,
        factura_url: formData.factura_url || null,
        tiene_comprobante: formData.tiene_comprobante,
        tiene_factura: formData.tiene_factura,
        facturador: trabajadorSeleccionado?.cuit
          ? formatearCUIT(trabajadorSeleccionado.cuit)
          : formData.facturador.trim() || null,
        factura_a_nombre_de: formData.factura_a_nombre_de.trim() || null,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        entrada_relacionada_id: entradaCreada.id,
        generado_desde_entrada: true,
      };

      const { data: cobroCreado, error: cobroError } = await supabase
        .from("cobros_trabajadores")
        .insert([cobroData])
        .select();

      if (cobroError) {
        console.error("Error creando cobro automático:", cobroError);
        showCustomAlert(
          "error",
          "Error",
          "Entrada creada pero hubo un error al crear el cobro automático: " +
            cobroError.message
        );
        return;
      }

      // Actualizar la entrada con la referencia al cobro
      await supabase
        .from("entradas")
        .update({ cobro_relacionado_id: cobroCreado[0].id })
        .eq("id", entradaCreada.id);

      // 🔧 AGREGAR: Manejar el balance de la caja manualmente
      // (Como los cobros normales funcionan, copiamos la misma lógica)
      await actualizarBalanceCajaParaCobro(
        cajaEmpresa.id,
        parseFloat(formData.monto)
      );

      console.log(
        "✅ Cobro automático creado y balance actualizado manualmente"
      );
    } catch (err) {
      console.error("Error en cobro automático:", err);
      showCustomAlert(
        "error",
        "Error",
        "Entrada creada pero hubo un error al crear el cobro automático: " +
          err.message
      );
    }
  };

  const actualizarBalanceCajaParaCobro = async (cajaId, monto) => {
    try {
      // Obtener balance actual de la caja
      const { data: caja, error: cajaError } = await supabase
        .from("cajas")
        .select("balance_actual")
        .eq("id", cajaId)
        .single();

      if (cajaError) {
        console.error("Error obteniendo balance de caja:", cajaError);
        return;
      }

      // Calcular nuevo balance (restar el monto del cobro)
      const balanceActual = parseFloat(caja.balance_actual) || 0;
      const nuevoBalance = balanceActual - monto;

      // Actualizar el balance
      const { error: updateError } = await supabase
        .from("cajas")
        .update({
          balance_actual: nuevoBalance,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cajaId);

      if (updateError) {
        console.error("Error actualizando balance de caja:", updateError);
      } else {
        console.log(
          `✅ Balance actualizado: ${balanceActual} - ${monto} = ${nuevoBalance}`
        );
      }
    } catch (err) {
      console.error("Error en actualización de balance para cobro:", err);
    }
  };

  const actualizarBalanceCajas = async () => {
    try {
      const montoOperacion = parseFloat(formData.monto);

      if (entrada) {
        const montoOriginal = parseFloat(entrada.monto);
        const cajaOrigenOriginalId = entrada.caja_origen_id;
        const cajaDestinoOriginalId = entrada.caja_destino_id;
        const monedaOriginal = entrada.moneda;

        if (
          montoOperacion === montoOriginal &&
          formData.caja_origen_id === cajaOrigenOriginalId &&
          formData.caja_destino_id === cajaDestinoOriginalId &&
          formData.moneda === monedaOriginal
        ) {
          return;
        }

        if (cajaDestinoOriginalId) {
          const { data: cajaDestinoOriginal, error: errorDestinoOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaDestinoOriginalId)
              .single();

          if (!errorDestinoOriginal && cajaDestinoOriginal) {
            const balanceDestinoOriginal =
              parseFloat(cajaDestinoOriginal.balance_actual) || 0;
            const balanceDestinoRevertido =
              balanceDestinoOriginal - montoOriginal;

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceDestinoRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaDestinoOriginalId);
          }
        }

        if (cajaOrigenOriginalId) {
          const { data: cajaOrigenOriginal, error: errorOrigenOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaOrigenOriginalId)
              .single();

          if (!errorOrigenOriginal && cajaOrigenOriginal) {
            const balanceOrigenOriginal =
              parseFloat(cajaOrigenOriginal.balance_actual) || 0;
            const balanceOrigenRevertido =
              balanceOrigenOriginal + montoOriginal;

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceOrigenRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaOrigenOriginalId);
          }
        }
      }

      // Aplicar la nueva operación
      if (formData.caja_destino_id) {
        const { data: cajaDestino, error: errorDestino } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_destino_id)
          .single();

        if (!errorDestino && cajaDestino) {
          const balanceActualDestino =
            parseFloat(cajaDestino.balance_actual) || 0;
          const nuevoBalanceDestino = balanceActualDestino + montoOperacion;

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceDestino,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_destino_id);
        }
      }

      if (
        formData.caja_origen_id &&
        formData.caja_origen_id !== formData.caja_destino_id
      ) {
        const { data: cajaOrigen, error: errorOrigen } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_origen_id)
          .single();

        if (!errorOrigen && cajaOrigen) {
          const balanceActualOrigen =
            parseFloat(cajaOrigen.balance_actual) || 0;
          const nuevoBalanceOrigen = balanceActualOrigen - montoOperacion;

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceOrigen,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_origen_id);
        }
      }
    } catch (err) {
      console.error("Error en actualización de balances:", err);
    }
  };

  // Preparar opciones para dropdowns
  const opcionesProyectos = proyectos.map((proyecto) => ({
    value: proyecto.id,
    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
      proyecto.obra_finalizada ? " (Finalizado)" : ""
    }`,
  }));

  const opcionesConceptos = useMemo(() => {
    return todasLosConceptos
      .filter((c) => c.id === formData.concepto_id || c.activo === true)
      .map((concepto) => ({
        value: concepto.id,
        label: concepto.nombre,
      }));
  }, [todasLosConceptos, formData.concepto_id]);

  const opcionesTrabajadores = trabajadores.map((trabajador) => ({
    value: trabajador.id,
    label: `${trabajador.apellido}, ${trabajador.nombre} - ${trabajador.puesto}`,
  }));

  const opcionesCajas = cajasDisponibles.map((caja) => ({
    value: caja.id,
    label: `${generarNombreCaja(caja)} ($${parseFloat(
      caja.balance_actual || 0
    ).toLocaleString()})`,
  }));

  // 1. Agregar la función actualizarCobroRelacionado dentro de EntradaModal
  const actualizarCobroRelacionado = async (cobroId, entradaActualizada) => {
    try {
      // Obtener el cobro actual
      const { data: cobroActual, error: errorCobro } = await supabase
        .from("cobros_trabajadores")
        .select("*")
        .eq("id", cobroId)
        .single();
      if (errorCobro || !cobroActual) {
        showCustomAlert(
          "error",
          "Error",
          "No se pudo obtener el cobro relacionado para actualizar"
        );
        return;
      }
      // Revertir el impacto anterior en la caja
      if (cobroActual.caja_origen_id) {
        const { data: cajaOrigen } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", cobroActual.caja_origen_id)
          .single();
        if (cajaOrigen) {
          const nuevoBalance =
            parseFloat(cajaOrigen.balance_actual) +
            parseFloat(cobroActual.monto);
          await supabase
            .from("cajas")
            .update({ balance_actual: nuevoBalance })
            .eq("id", cobroActual.caja_origen_id);
        }
      }
      // Actualizar el cobro con los nuevos datos
      const cajaEmpresa = cajas.find(
        (caja) =>
          caja.tipo_caja === "empresa" && caja.moneda === formData.moneda
      );
      if (!cajaEmpresa) {
        showCustomAlert(
          "error",
          "Error",
          "No se encontró caja empresa para el cobro automático"
        );
        return;
      }
      // Buscar concepto "Honorarios"
      const { data: conceptosCobro } = await supabase
        .from("conceptos_cobro")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true);
      let conceptoHonorarios = conceptosCobro.find((c) =>
        c.nombre.toLowerCase().includes("honorarios")
      );
      if (!conceptoHonorarios) {
        const { data: nuevoConcepto } = await supabase
          .from("conceptos_cobro")
          .insert([
            {
              constructora_id: constructora.id,
              nombre: "Honorarios",
              activo: true,
            },
          ])
          .select()
          .single();
        conceptoHonorarios = nuevoConcepto;
      }
      const trabajadorSeleccionado = trabajadores.find(
        (t) => t.id === formData.trabajador_id
      );
      const cobroData = {
        trabajador_id: formData.trabajador_id,
        concepto_id: conceptoHonorarios.id,
        caja_origen_id: cajaEmpresa.id,
        monto: parseFloat(formData.monto),
        moneda: formData.moneda,
        metodo_pago: formData.metodo_pago || "Transferencia",
        fecha: formData.fecha,
        descripcion: `Pago directo desde cliente - ${
          formData.descripcion || "Sin descripción"
        }`,
        comprobante_url: formData.comprobante_url || null,
        factura_url: formData.factura_url || null,
        tiene_comprobante: formData.tiene_comprobante,
        tiene_factura: formData.tiene_factura,
        facturador: trabajadorSeleccionado?.cuit
          ? formatearCUIT(trabajadorSeleccionado.cuit)
          : formData.facturador.trim() || null,
        factura_a_nombre_de: formData.factura_a_nombre_de.trim() || null,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        generado_desde_entrada: true,
        entrada_relacionada_id: entradaActualizada.id,
      };
      await supabase
        .from("cobros_trabajadores")
        .update(cobroData)
        .eq("id", cobroId);
      // Restar el nuevo monto a la caja
      if (cajaEmpresa.id) {
        const { data: cajaEmpresaData } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", cajaEmpresa.id)
          .single();
        if (cajaEmpresaData) {
          const nuevoBalance =
            parseFloat(cajaEmpresaData.balance_actual) -
            parseFloat(formData.monto);
          await supabase
            .from("cajas")
            .update({ balance_actual: nuevoBalance })
            .eq("id", cajaEmpresa.id);
        }
      }
    } catch (err) {
      showCustomAlert(
        "error",
        "Error",
        "Error actualizando cobro relacionado: " + err.message
      );
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "16px" : "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <TrendingUp size={24} />
            <div>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "700",
                }}
              >
                {entrada ? "Editar entrada" : "Nueva entrada"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                }}
              >
                {entrada
                  ? "Modificar registro de entrada"
                  : "Registrar un nuevo ingreso"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "12px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "16px" : "20px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Proyecto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Proyecto *
              </label>
              <CustomDropdown
                value={formData.proyecto_id}
                onChange={(value) =>
                  setFormData({ ...formData, proyecto_id: value })
                }
                options={opcionesProyectos}
                placeholder="Selecciona un proyecto"
                disabled={permisosCliente?.proyectoId}
                icon={Building2}
              />
              {permisosCliente?.proyectoId && (
                <p
                  style={{
                    fontSize: "11px",
                    color: "#3b82f6",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Proyecto preseleccionado: {permisosCliente.proyectoCodigo}
                </p>
              )}
            </div>

            {/* Concepto */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Concepto *
                </label>
              </div>

              <CustomDropdown
                value={formData.concepto_id}
                onChange={(value) =>
                  setFormData({ ...formData, concepto_id: value })
                }
                options={opcionesConceptos}
                placeholder="Selecciona o busca un concepto"
                icon={FileText}
                canCreate={true}
                onCreateNew={crearNuevoConcepto}
                createText="Crear concepto"
              />
            </div>

            {/* Pago directo - Solo si es honorarios */}
            {formData.concepto_id &&
              (() => {
                const conceptoSeleccionado = conceptos.find(
                  (c) => c.id === formData.concepto_id
                );
                const esHonorarios = conceptoSeleccionado?.nombre
                  .toLowerCase()
                  .includes("honorarios");

                if (!esHonorarios) return null;

                return (
                  <div
                    style={{
                      background: "#fefbf3",
                      border: "2px solid #fed7aa",
                      borderRadius: "16px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#9a3412",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <User size={16} />
                        Pago directo
                      </label>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#9a3412",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.pago_directo_trabajador}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              pago_directo_trabajador: e.target.checked,
                              trabajador_id: e.target.checked
                                ? formData.trabajador_id
                                : "",
                            })
                          }
                          style={{
                            width: "16px",
                            height: "16px",
                            accentColor: "#ea580c",
                          }}
                        />
                        El cliente pagó directo al trabajador
                      </label>
                    </div>

                    {formData.pago_directo_trabajador && (
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#9a3412",
                          }}
                        >
                          Trabajador que recibió el pago *
                        </label>
                        <CustomDropdown
                          value={formData.trabajador_id}
                          onChange={(value) =>
                            setFormData({ ...formData, trabajador_id: value })
                          }
                          options={opcionesTrabajadores}
                          placeholder="Selecciona un trabajador"
                          icon={User}
                        />
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9a3412",
                            margin: "8px 0 0 0",
                            fontStyle: "italic",
                          }}
                        >
                          Se creará automáticamente un cobro de "Honorarios"
                          para este trabajador
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}

            {/* Monto y Moneda */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto *
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  step="0.01"
                  value={formData.monto}
                  onChange={(e) =>
                    setFormData({ ...formData, monto: e.target.value })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Moneda *
                </label>
                <CustomDropdown
                  value={formData.moneda}
                  onChange={(value) =>
                    setFormData({ ...formData, moneda: value })
                  }
                  options={[
                    { value: "ARS", label: "ARS - Pesos Argentinos" },
                    { value: "USD", label: "USD - Dólares" },
                  ]}
                  placeholder="Selecciona moneda"
                  icon={DollarSign}
                />
              </div>
            </div>

            {/* Fecha y Tipo de Cambio */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Fecha *
                </label>
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Tipo de cambio *{" "}
                  {loadingDolar && (
                    <Loader2 size={12} className="loading-pulse" />
                  )}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  step="0.01"
                  value={formData.tipo_cambio}
                  onChange={(e) =>
                    setFormData({ ...formData, tipo_cambio: e.target.value })
                  }
                  placeholder="1.00"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "none";
                  }}
                />
                {dolarOficial && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#10b981",
                      margin: "4px 0 0 0",
                      fontWeight: "500",
                    }}
                  >
                    Dólar oficial: ${dolarOficial}
                  </p>
                )}
                {formData.monto && calcularEquivalente() && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#10b981",
                      margin: "2px 0 0 0",
                      fontWeight: "600",
                    }}
                  >
                    {calcularEquivalente()}
                  </p>
                )}
              </div>
            </div>

            {/* Cajas de Origen y Destino */}
            {cajasDisponibles.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Caja de origen
                  </label>
                  <CustomDropdown
                    value={formData.caja_origen_id}
                    onChange={(value) =>
                      setFormData({ ...formData, caja_origen_id: value })
                    }
                    options={[
                      { value: "", label: "Cliente" },
                      ...opcionesCajas,
                    ]}
                    placeholder="Selecciona origen"
                    icon={Wallet}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Caja de destino *
                  </label>
                  <CustomDropdown
                    value={formData.caja_destino_id}
                    onChange={(value) =>
                      setFormData({ ...formData, caja_destino_id: value })
                    }
                    options={opcionesCajas}
                    placeholder="Selecciona destino"
                    icon={Wallet}
                  />
                </div>
              </div>
            )}

            {/* Método de Pago */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Método de pago
              </label>
              <CustomDropdown
                value={formData.metodo_pago}
                onChange={(value) =>
                  setFormData({ ...formData, metodo_pago: value })
                }
                options={[
                  { value: "transferencia", label: "Transferencia" },
                  { value: "efectivo", label: "Efectivo" },
                  { value: "cheque", label: "Cheque" },
                  { value: "deposito", label: "Depósito" },
                  { value: "mercadopago", label: "MercadoPago" },
                  { value: "otro", label: "Otro" },
                ]}
                placeholder="Selecciona método"
                icon={DollarSign}
              />
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción adicional de la entrada..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3b82f6";
                  e.target.style.boxShadow =
                    "0 0 0 3px rgba(59, 130, 246, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Comprobante */}
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "16px",
                border: "2px solid #f1f5f9",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <FileText size={16} />
                  Comprobante
                </label>
                <button
                  type="button"
                  aria-pressed={Boolean(formData.tiene_comprobante)}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      tiene_comprobante: !prev.tiene_comprobante,
                      comprobante_url: !prev.tiene_comprobante ? prev.comprobante_url : "",
                    }))
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    border: '1px solid ' + (formData.tiene_comprobante ? '#10b981' : '#cbd5e1'),
                    background: formData.tiene_comprobante ? '#10b981' : '#e2e8f0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all .2s ease',
                    outline: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: formData.tiene_comprobante ? 22 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '9999px',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left .2s ease',
                    }}
                  />
                </button>
              </div>

              {formData.tiene_comprobante && (
                <div
                  style={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: "12px",
                    padding: "20px",
                    textAlign: "center",
                    background: "white",
                  }}
                >
                  {formData.comprobante_url ? (
                    <div>
                      <CheckCircle2
                        size={32}
                        color="#10b981"
                        style={{ marginBottom: "12px" }}
                      />
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#10b981",
                          margin: "0 0 12px 0",
                          fontWeight: "600",
                        }}
                      >
                        Comprobante subido correctamente
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            window.open(formData.comprobante_url, "_blank")
                          }
                          className="button-hover"
                          style={{
                            background: "#eff6ff",
                            border: "2px solid #3b82f6",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            color: "#3b82f6",
                            fontSize: "12px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          Ver archivo
                        </button>
                        <button
                          onClick={() =>
                            setFormData({ ...formData, comprobante_url: "" })
                          }
                          className="button-hover"
                          style={{
                            background: "#fef2f2",
                            border: "2px solid #ef4444",
                            borderRadius: "8px",
                            padding: "8px 16px",
                            color: "#ef4444",
                            fontSize: "12px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          Quitar archivo
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      
                      {/* Simplificado: solo botón para seleccionar comprobante */}
                      <input
                        type="file"
                        id="comprobante-upload"
                        accept="image/*,application/pdf"
                        onChange={(e) =>
                          handleFileUpload(e.target.files[0], "comprobante")
                        }
                        style={{ display: "none" }}
                        disabled={subiendoComprobante}
                      />
                      <label
                        htmlFor="comprobante-upload"
                        className="button-hover"
                        style={{
                          background: subiendoComprobante
                            ? "#94a3b8"
                            : "#10b981",
                          color: "white",
                          padding: "12px 20px",
                          borderRadius: "12px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: subiendoComprobante
                            ? "not-allowed"
                            : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          touchAction: "manipulation",
                          border: "none",
                        }}
                      >
                        {subiendoComprobante ? (
                          <Loader2 size={16} className="loading-pulse" />
                        ) : (
                          <Upload size={16} />
                        )}
                        {subiendoComprobante
                          ? "Subiendo..."
                          : "Seleccionar comprobante"}
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Factura */}
            <div
              style={{
                background: "#fefbf3",
                padding: "20px",
                borderRadius: "16px",
                border: "2px solid #fed7aa",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <FileText size={16} />
                  Factura
                </label>
                <button
                  type="button"
                  aria-pressed={Boolean(formData.tiene_factura)}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      tiene_factura: !prev.tiene_factura,
                      factura_url: !prev.tiene_factura ? prev.factura_url : "",
                      facturador: !prev.tiene_factura ? prev.facturador : "",
                      factura_a_nombre_de: !prev.tiene_factura ? prev.factura_a_nombre_de : "",
                      tipo_factura: !prev.tiene_factura ? (prev.tipo_factura || "A") : "A",
                    }))
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    border: '1px solid ' + (formData.tiene_factura ? '#eab308' : '#cbd5e1'),
                    background: formData.tiene_factura ? '#eab308' : '#e2e8f0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all .2s ease',
                    outline: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: formData.tiene_factura ? 22 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '9999px',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left .2s ease',
                    }}
                  />
                </button>
              </div>

              {formData.tiene_factura && (
                <div style={{ display: "grid", gap: "16px" }}>
                  {/* Datos de facturación */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "8px",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        Tipo de factura
                      </label>
                      <CustomDropdown
                        value={formData.tipo_factura}
                        onChange={(value) => setFormData({ ...formData, tipo_factura: value })}
                        options={[
                          { value: "A", label: "Factura A" },
                          { value: "B", label: "Factura B" },
                          { value: "C", label: "Factura C" }
                        ]}
                        placeholder="Selecciona tipo de factura"
                        icon={FileText}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        CUIT Facturador *
                      </label>
                      <input
                        type="text"
                        value={formData.facturador}
                        onChange={(e) =>
                          handleCUITChange(e.target.value, "facturador")
                        }
                        placeholder="XX-XXXXXXXX-X"
                        maxLength="13"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #eab308",
                          borderRadius: "12px",
                          fontSize: "16px",
                          boxSizing: "border-box",
                          background: "white",
                          outline: "none",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          touchAction: "manipulation",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#d97706";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(234, 179, 8, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#eab308";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {formData.facturador &&
                        obtenerNombrePorCuit(formData.facturador) && (
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#10b981",
                              margin: "4px 0 0 0",
                              fontWeight: "500",
                            }}
                          >
                            {obtenerNombrePorCuit(formData.facturador)}
                          </p>
                        )}
                      {formData.pago_directo_trabajador &&
                        formData.trabajador_id && (
                          <p
                            style={{
                              fontSize: "10px",
                              color: "#3b82f6",
                              margin: "4px 0 0 0",
                              fontStyle: "italic",
                            }}
                          >
                            Se cargará automáticamente el CUIT del trabajador
                            seleccionado
                          </p>
                        )}
                    </div>
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        CUIT Factura a nombre de *
                      </label>
                      <input
                        type="text"
                        value={formData.factura_a_nombre_de}
                        onChange={(e) =>
                          handleCUITChange(
                            e.target.value,
                            "factura_a_nombre_de"
                          )
                        }
                        placeholder="XX-XXXXXXXX-X"
                        maxLength="13"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "2px solid #eab308",
                          borderRadius: "12px",
                          fontSize: "16px",
                          boxSizing: "border-box",
                          background: "white",
                          outline: "none",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          touchAction: "manipulation",
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#d97706";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(234, 179, 8, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#eab308";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {formData.factura_a_nombre_de &&
                        obtenerNombrePorCuit(formData.factura_a_nombre_de) && (
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#10b981",
                              margin: "4px 0 0 0",
                              fontWeight: "500",
                            }}
                          >
                            {obtenerNombrePorCuit(formData.factura_a_nombre_de)}
                          </p>
                        )}
                    </div>
                  </div>

                  {/* Upload de factura */}
                  <div
                    style={{
                      border: "2px dashed #eab308",
                      borderRadius: "12px",
                      padding: "20px",
                      textAlign: "center",
                      background: "white",
                    }}
                  >
                    {formData.factura_url ? (
                      <div>
                        <CheckCircle2
                          size={32}
                          color="#eab308"
                          style={{ marginBottom: "12px" }}
                        />
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#eab308",
                            margin: "0 0 12px 0",
                            fontWeight: "600",
                          }}
                        >
                          Factura subida correctamente
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() =>
                              window.open(formData.factura_url, "_blank")
                            }
                            className="button-hover"
                            style={{
                              background: "#fefbf3",
                              border: "2px solid #eab308",
                              borderRadius: "8px",
                              padding: "8px 16px",
                              color: "#eab308",
                              fontSize: "12px",
                              cursor: "pointer",
                              fontWeight: "600",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                            }}
                          >
                            Ver factura
                          </button>
                          <button
                            onClick={() =>
                              setFormData({ ...formData, factura_url: "" })
                            }
                            className="button-hover"
                            style={{
                              background: "#fef2f2",
                              border: "2px solid #ef4444",
                              borderRadius: "8px",
                              padding: "8px 16px",
                              color: "#ef4444",
                              fontSize: "12px",
                              cursor: "pointer",
                              fontWeight: "600",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                            }}
                          >
                            Quitar factura
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {/* Simplificado: solo botón para seleccionar factura */}
                        <input
                          type="file"
                          id="factura-upload"
                          accept="image/*,application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e.target.files[0], "factura")
                          }
                          style={{ display: "none" }}
                          disabled={subiendoFactura}
                        />
                        <label
                          htmlFor="factura-upload"
                          className="button-hover"
                          style={{
                            background: subiendoFactura ? "#94a3b8" : "#eab308",
                            color: "white",
                            padding: "12px 20px",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: subiendoFactura ? "not-allowed" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                            border: "none",
                          }}
                        >
                          {subiendoFactura ? (
                            <Loader2 size={16} className="loading-pulse" />
                          ) : (
                            <Upload size={16} />
                          )}
                          {subiendoFactura
                            ? "Subiendo..."
                            : "Seleccionar factura"}
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "20px" : "25px",
            borderTop: "2px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "12px 20px" : "14px 24px",
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            Cancelar
          </button>

          <button
            onClick={handleSave}
            disabled={guardando || subiendoComprobante || subiendoFactura}
            className="button-hover"
            style={{
              padding: isMobile ? "12px 24px" : "14px 28px",
              border: "none",
              borderRadius: "12px",
              background:
                guardando || subiendoComprobante || subiendoFactura
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                guardando || subiendoComprobante || subiendoFactura
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando entrada...
              </>
            ) : (
              <>
                <Save size={16} />
                Guardar entrada
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
}

export default Entradas;
